import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  Check,
  Phone,
  TrendingUp,
  XCircle,
  Eye,
  Sparkles,
  Trophy,
  Skull,
  Bot,
  Swords,
  Coins,
  BadgeCheck,
  RotateCcw,
  History,
  Copy,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { AudioProvider, useAudio } from '../poker-ui/AudioProvider';
import { GameIndustrialHeader } from '../poker-ui/GameIndustrialHeader';
import { ArcadeModeBackdrop } from '../poker-ui/ArcadeModeBackdrop';
import { IndustrialSeat } from '../poker-ui/IndustrialSeat';
import { TableSeatProfileModal } from '../poker-ui/TableSeatProfileModal';
import { PokerTableSurface } from '../poker-ui/PokerTableSurface';
import { PlayerHandStrip } from '../poker-ui/PlayerHandStrip';
import { SekaActionBar } from '../poker-ui/SekaActionBar';
import { PokerBetRaiseSlider } from '../poker-ui/PokerBetRaiseSlider';
import { cn } from '../poker-ui/utils/cn';
import {
  buildEllipticSeatSlots,
  getSeatDensityPreset,
  getSeatArenaTuning,
} from '../poker-ui/seatLayout';
import {
  clampSeatCenter,
  seatDisplayPercents,
  loadOffsetsForBlock,
  saveOffsetsForBlock,
  clearOffsetsForBlock,
  SEAT_OFFSET_STORAGE,
} from '../poker-ui/seatLayoutOffsets';

import { buildAllowedActionsForLocalCurrentTurn } from '../game/sekaAllowedActions';
import { compareSekaHands, DEFAULT_SEKA_CONFIG, LocalSekaGame } from '../game/sekaEngine';
import { SEKA_HERO_TURN_BUDGET_MS, SEKA_HERO_TURN_SECONDS_CEIL } from '../game/sekaTurnBudget';
import { PlayingCard } from '../poker-ui/PlayingCard';
import '../poker-ui/sekaAnteBurst.css';
import './Lobby.css';
import {
  appendSekaMatchHistoryExportRow as appendMatchHistoryRowLines,
  buildPlayerSnapshotsFromHistoryEntry,
  describeSekaHandEndType,
  formatHistoryAction,
  SEKA_HISTORY_EXPORT_HINT_LINE,
  sekaCardToPlaying,
} from '../game/sekaHistoryUi';
import { MatchChronicleChain, MatchChronicleVertical } from '../poker-ui/MatchChronicleChain';
import { LiveSekaHandMovesPanel, LiveSekaHandMovesTrail } from '../poker-ui/LiveSekaHandMovesPanel';
import { LocalSekaLeaveModal } from './LocalSekaLeaveModal';
import { SessionResultOverlay } from './GameEffects';
import { buildLocalSekaBotSessionOutcome } from '../game/sessionOutcomeLocal';

export { LocalSekaGame };

const EMPTY_COMMUNITY = [null, null, null, null, null];
const BOT_COUNT_OPTIONS = [1, 2, 3];
const STARTING_STACK_OPTIONS = [500, 1000, 2000, 5000];
const ANTE_OPTIONS = [10, 20, 50, 100];
const SVARKA_COMMIT_SECONDS = 7;
/**
 * Текст фазы анте: без «−800», чтобы не путать с уходом баланса в минус;
 * при частичном анте (isAllInOnAnte) — номинал + авто-вскрытие.
 */
function buildLocalAntePhaseBannerText(mult, eff, eligiblePlayers) {
  const m = Math.max(1, Number(mult) || 1);
  const effStr = Number(eff).toLocaleString('ru-RU');
  const anyShortAnte = Array.isArray(eligiblePlayers)
    ? eligiblePlayers.some((p) => Boolean(p?.isAllInOnAnte))
    : false;

  if (anyShortAnte) {
    if (m > 1) {
      return `Свара ×${m}: номинал ${effStr} SEKA · авто-вскрытие (all-in анте)`;
    }
    return `Взнос: номинал ${effStr} SEKA · авто-вскрытие (all-in анте)`;
  }

  if (m > 1) {
    return `Свара ×${m} · анте ${effStr} SEKA с каждого`;
  }
  return `Анте: ${effStr} SEKA с каждого`;
}

function findClosestOptionIndex(options, value) {
  const directIdx = options.indexOf(value);
  if (directIdx >= 0) return directIdx;
  const numeric = Number(value);
  let bestIdx = 0;
  let bestDistance = Number.POSITIVE_INFINITY;
  options.forEach((opt, idx) => {
    const distance = Math.abs(Number(opt) - numeric);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIdx = idx;
    }
  });
  return bestIdx;
}

/** Строки разбора раздачи: карты и комбинации всех участников (победа / поражение / ничья). Победители — первыми в списке. */
function buildShowdownRows(game, seatStartChips = null) {
  const isNoEntryTerminal = Boolean(
    game?.gameEnded &&
      !game?.winner &&
      (!Array.isArray(game?.winners) || game.winners.length === 0)
  );
  const rows = game.players
    .map((p, seatIdx) => {
      if (!p) return null;
      const nameShort = p.name.length > 10 ? `${p.name.slice(0, 9)}…` : p.name;
      const cards = (p.cards || []).map(sekaCardToPlaying).filter(Boolean);
      const startChipsRaw = Array.isArray(seatStartChips) ? Number(seatStartChips[seatIdx]) : Number.NaN;
      const startChips = Number.isFinite(startChipsRaw) ? startChipsRaw : null;
      const isWinner = Boolean(
        (game.winner && game.winner.id === p.id) ||
          (game.winners || []).some((w) => w.id === p.id)
      );
      return {
        key: p.id,
        seatIdx,
        nameShort,
        cards,
        description: game.evaluateHand(p.cards).description,
        isWinner,
        folded: Boolean(p.isFolded),
        didNotEnter: isNoEntryTerminal && Boolean(p.isFolded) && cards.length === 0,
        allInAnte: Boolean(p.isAllInOnAnte),
        isHero: seatIdx === 0,
        chipsDelta: startChips == null ? null : (Number(p.chips) || 0) - startChips,
      };
    })
    .filter(Boolean);
  rows.sort((a, b) => {
    if (a.isWinner !== b.isWinner) return a.isWinner ? -1 : 1;
    return a.seatIdx - b.seatIdx;
  });
  return rows;
}

/**
 * Короткое объяснение, почему банк не у героя (место 0, id 1).
 */
function buildLossShowdownExplain(game) {
  const players = game.players.filter(Boolean);
  const hero = players[0];
  if (!hero) return '';

  if (game.gameEnded && players.some((p) => p.isAllInOnAnte)) {
    return 'Раздача сразу вскрыта: не у всех хватило фишек на полное анте (all-in анте). Победитель по сильнейшей комбинации.';
  }

  const active = players.filter((p) => !p.isFolded);
  const winner = game.winner;
  const winners = game.winners?.length ? [...game.winners] : winner ? [winner] : [];

  if (active.length === 1) {
    const surv = active[0];
    if (hero.isFolded) {
      return `Остальные сбросили — банк без вскрытия у ${surv.name}. Вы уже не участвовали в розыгрыше банка.`;
    }
    return `Остальные сбросили — банк без вскрытия у ${surv.name}.`;
  }

  if (active.length === 0 && !winner && winners.length === 0) {
    return 'Никто не подтвердил вход в следующую раздачу — розыгрыш банка не состоялся.';
  }

  if (hero.isFolded) {
    if (winner) {
      const wh = game.evaluateHand(winner.cards).description;
      return `Вы сбросили до вскрытия. Среди оставшихся банк у ${winner.name}: ${wh}.`;
    }
    if (winners.length > 1) {
      return `Вы сбросили до вскрытия. Ничья на вскрытии: ${winners.map((w) => w.name).join(' · ')}.`;
    }
    return 'Вы сбросили до вскрытия.';
  }

  if (!winner && winners.length > 1) {
    const heroHand = game.evaluateHand(hero.cards);
    const topStrength = Math.max(...winners.map((w) => game.evaluateHand(w.cards).strength));
    if (heroHand.strength < topStrength) {
      return `Ничья на сильнейшей руке между ${winners.map((w) => w.name).join(' · ')}. У вас по силе слабее: ${heroHand.description}.`;
    }
    return `Ничья: ${winners.map((w) => w.name).join(' · ')}. У вас: ${heroHand.description}.`;
  }

  if (winner && !hero.isFolded) {
    const hw = game.evaluateHand(hero.cards);
    const ww = game.evaluateHand(winner.cards);
    if (compareSekaHands(winner.cards, hero.cards) > 0) {
      return `При вскрытии у ${winner.name} сильнее комбинация (${ww.description}), чем у вас (${hw.description}).`;
    }
  }

  return '';
}

/**
 * Короткое объяснение победы героя (место 0, id 1): почему банк у вас.
 */
function buildWinShowdownExplain(game) {
  const players = game.players.filter(Boolean);
  const hero = players[0];
  if (!hero) return '';

  if (game.gameEnded && players.some((p) => p.isAllInOnAnte)) {
    return 'Раздача сразу вскрыта из-за all-in анте — банк достаётся сильнейшей комбинации.';
  }

  const active = players.filter((p) => !p.isFolded);
  const winner = game.winner;

  if (active.length === 1 && active[0].id === hero.id) {
    return 'Соперники сбросили — банк без вскрытия достаётся вам.';
  }

  if (winner && winner.id === hero.id && !hero.isFolded) {
    const hw = game.evaluateHand(hero.cards);
    const others = players.filter((p) => p.id !== hero.id && !p.isFolded);
    if (others.length === 0) {
      return `Банк ваш: ${hw.description}.`;
    }
    const ranked = others
      .map((p) => ({ p, h: game.evaluateHand(p.cards) }))
      .sort((a, b) => b.h.strength - a.h.strength);
    const bestOther = ranked[0];
    if (bestOther && compareSekaHands(hero.cards, bestOther.p.cards) > 0) {
      return `При вскрытии ваша комбинация (${hw.description}) сильнее, чем у ${bestOther.p.name} (${bestOther.h.description}).`;
    }
    return `При вскрытии: ${hw.description}.`;
  }

  return '';
}

/** Пояснение при ничьей с участием героя. */
function buildChopShowdownExplain(game) {
  const players = game.players.filter(Boolean);
  const hero = players[0];

  if (game.gameEnded && players.some((p) => p.isAllInOnAnte)) {
    return 'Раздача сразу вскрыта из-за all-in анте — ничья на сильнейшей руке, банк делится.';
  }
  const winners = game.winners?.length ? [...game.winners] : [];
  if (!hero || winners.length < 2) return '';

  const hw = game.evaluateHand(hero.cards);
  const names = winners.map((w) => w.name).join(' · ');
  return `Ничья на вскрытии между ${names} на силе руки «${hw.description}». Банк делится поровну между победителями.`;
}

function buildPlayerSeeds(heroName, botCount) {
  return [
    { id: 1, name: heroName || 'Игрок' },
    ...Array.from({ length: botCount }, (_, i) => ({
      id: i + 2,
      name: `Бот ${i + 1}`,
    })),
  ];
}

function cloneLocalGame(game) {
  const cloned = new LocalSekaGame({}, game.players.map((p) => ({ id: p.id, name: p.name })));
  cloned.players = JSON.parse(JSON.stringify(game.players));
  cloned.currentPlayer = game.currentPlayer;
  cloned.pot = game.pot;
  cloned.currentBet = game.currentBet;
  cloned.roundNumber = game.roundNumber;
  cloned.gamePhase = game.gamePhase;
  cloned.gameEnded = game.gameEnded;
  cloned.winner = game.winner;
  cloned.winners = game.winners ? [...game.winners] : [];
  cloned.dealerIndex = game.dealerIndex;
  cloned.lastAggressorIndex = game.lastAggressorIndex;
  cloned.playersActedThisRound = new Set(game.playersActedThisRound || []);
  cloned.config = game.config;
  cloned.gameHistory = [...game.gameHistory];
  cloned.deck = [...game.deck];
  cloned.lastAnteContributions = Array.isArray(game.lastAnteContributions)
    ? game.lastAnteContributions.map((c) => ({ ...c }))
    : [];
  cloned.svarkaAnteMultiplier =
    game.svarkaAnteMultiplier != null ? Number(game.svarkaAnteMultiplier) : 1;
  cloned.handHadRaise = Boolean(game.handHadRaise);
  cloned.svarkaChainCount =
    game.svarkaChainCount != null ? Number(game.svarkaChainCount) : 0;
  cloned.currentHandActionLog = Array.isArray(game.currentHandActionLog)
    ? game.currentHandActionLog.map((a) => ({ ...a }))
    : [];
  cloned._moveSeq = game._moveSeq != null ? Number(game._moveSeq) : 0;
  cloned.currentHandStartStacks = Array.isArray(game.currentHandStartStacks)
    ? game.currentHandStartStacks.map((s) => ({ ...s }))
    : null;
  return cloned;
}

const LOCAL_FAST_FORWARD_GUARD = 8000;

/**
 * Досимуляция до конца раздачи на клоне: герой — пас (или чек/колл, если паса нет), боты — {@link botDecision}.
 * Не вызывать в фазе {@code svarkaBetween} (нет допустимых ходов в UI-движке).
 * @param {import('../game/sekaEngine').LocalSekaGame} startGame
 * @returns {import('../game/sekaEngine').LocalSekaGame}
 */
function fastForwardLocalGameToEnded(startGame) {
  let g = cloneLocalGame(startGame);
  let guard = 0;
  while (!g.gameEnded && guard++ < LOCAL_FAST_FORWARD_GUARD) {
    if (g.gamePhase === 'svarkaBetween') break;
    const allowed = buildAllowedActionsForLocalCurrentTurn(g);
    if (!allowed.length) break;
    const seat = g.currentPlayer;
    if (seat === 0) {
      const byUpper = (u) => allowed.find((a) => String(a?.action || '').toUpperCase() === u);
      const act = byUpper('FOLD') || byUpper('CHECK') || byUpper('CALL') || allowed[0];
      const upper = String(act?.action || '').toUpperCase();
      let amount = 0;
      if (upper === 'RAISE') {
        const p = g.players[0];
        const step = g.config?.minRaise ?? 10;
        const minRaise = g.currentBet + step;
        const maxRaise = Math.min(p.chips * (p.isDark ? 2 : 1), g.currentBet + p.chips);
        amount = Math.min(maxRaise, Math.max(minRaise, minRaise));
      }
      g.makeMove(0, upper, amount);
    } else {
      const move = botDecision(g, seat);
      const upper = String(move.action || '').toUpperCase();
      let amount = Number(move.amount) || 0;
      if (upper === 'RAISE') {
        const p = g.players[seat];
        const step = g.config?.minRaise ?? 10;
        const minRaise = g.currentBet + step;
        const maxRaise = Math.min(p.chips * (p.isDark ? 2 : 1), g.currentBet + p.chips);
        if (!amount || amount < minRaise) {
          amount = Math.min(maxRaise, Math.max(minRaise, minRaise));
        }
      }
      g.makeMove(seat, upper, amount);
    }
  }
  return g;
}

function buildHistoryKey(entry, fallback = 0) {
  if (!entry) return `empty-${fallback}`;
  const tsRaw = entry.timestamp;
  const ts =
    typeof tsRaw === 'string' || typeof tsRaw === 'number'
      ? String(tsRaw)
      : tsRaw instanceof Date
        ? String(tsRaw.getTime())
        : 'no-ts';
  const pot = Number(entry.pot || 0);
  const winner = entry.winner?.id || entry.winner?.name || 'draw';
  const winnersCount = Array.isArray(entry.winners) ? entry.winners.length : 0;
  const actionsCount = Array.isArray(entry.actions) ? entry.actions.length : 0;
  return `${ts}|${pot}|${winner}|${winnersCount}|${actionsCount}`;
}

function mergeHistoryUnique(prev, incoming) {
  const base = Array.isArray(prev) ? prev : [];
  const append = Array.isArray(incoming) ? incoming : [];
  if (append.length === 0) return base;
  const seen = new Set(base.map((h, i) => buildHistoryKey(h, i)));
  const merged = [...base];
  append.forEach((h, i) => {
    const key = buildHistoryKey(h, i + base.length);
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(h);
  });
  return merged;
}

function winnerNamesFromHistoryEntry(hist) {
  if (!hist) return '—';
  if (Array.isArray(hist.winners) && hist.winners.length > 0) {
    return hist.winners.map((w) => w?.name).filter(Boolean).join(' · ');
  }
  return hist.winner?.name || 'Ничья';
}

/** Решение места в локальной сваре (голосование перед раздачей). */
function describeSvarkaSeatChoice(p, seatIndex, participate, effAnte) {
  const chips = Number(p?.chips ?? 0);
  const canParticipate = !Boolean(p?.isSittingOut) && chips > 0;
  const name = p?.name || `Место ${seatIndex}`;
  if (!canParticipate) return `${name}: не в розыгрыше (нет фишек или сит-аут)`;
  const pay = Math.min(Number(effAnte) || 0, chips);
  if (participate === true) return `${name}: играет (взнос до ${pay.toLocaleString('ru-RU')} SEKA)`;
  return `${name}: пропуск (пас в сваре)`;
}

/** Текстовый дамп матча для буфера обмена (аналитика, баг-репорты). */
function buildFullMatchHistoryExportText(matchChronicle, matchHistoryRowsUnfiltered) {
  const lines = [];
  lines.push('=== Сека — хроника матча (локально) ===');
  lines.push(`Дата экспорта: ${new Date().toLocaleString('ru-RU')}`);
  lines.push('');
  lines.push(SEKA_HISTORY_EXPORT_HINT_LINE);
  lines.push('');

  if (Array.isArray(matchChronicle) && matchChronicle.length > 0) {
    lines.push('--- Хроника матча ---');
    matchChronicle.forEach((entry, i) => {
      lines.push(`${i + 1}. ${entry.title}`);
      if (entry.subtitle) lines.push(`   ${entry.subtitle}`);
      const stepLines = Array.isArray(entry.steps) ? entry.steps : [];
      stepLines.forEach((s, j) => lines.push(`   ${j + 1}) ${s}`));
      lines.push('');
    });
  }

  const rows = Array.isArray(matchHistoryRowsUnfiltered) ? matchHistoryRowsUnfiltered : [];
  lines.push('--- Раздачи (по порядку) ---');
  if (rows.length === 0) {
    lines.push('(раздач пока нет)');
  } else {
    rows.forEach((row) => appendMatchHistoryRowLines(lines, row));
  }

  return lines.join('\n');
}

/** Снимок для окна победы/поражения: хвост хроники + последняя раздача. */
function buildOutcomePanelExportText(matchChronicle, lastRow) {
  const lines = [];
  lines.push('=== Сека — снимок после раздачи ===');
  lines.push(`Время: ${new Date().toLocaleString('ru-RU')}`);
  lines.push('');
  const tail = Array.isArray(matchChronicle) ? matchChronicle.slice(-12) : [];
  if (tail.length > 0) {
    lines.push('--- Последние события матча (хроника) ---');
    tail.forEach((entry, i) => {
      lines.push(`${i + 1}. ${entry.title}`);
      if (entry.subtitle) lines.push(`   ${entry.subtitle}`);
      (entry.steps || []).forEach((s, j) => lines.push(`   ${j + 1}) ${s}`));
      lines.push('');
    });
  }
  lines.push('--- Эта раздача ---');
  if (lastRow) {
    appendMatchHistoryRowLines(lines, lastRow);
  } else {
    lines.push('(сводка раздачи появится после синхронизации истории)');
  }
  return lines.join('\n');
}

/**
 * Номиналы на столе: у героя (место 0) — после LOOK или выхода из тёмную;
 * у соперников — только после окончания раздачи (вскрытие). Статус isDark в движке не трогаем.
 */
function shouldShowSeatCardFaces(seat, p, showCardsArr, gameEnded) {
  if (!p?.cards?.length) return false;
  if (gameEnded) {
    if (seat === 0) return true;
    return !p.isFolded;
  }
  if (seat === 0) return !p.isDark || Boolean(showCardsArr?.[seat]);
  return false;
}

/** Рубашка по слоту: анимация раздачи + скрытие чужих карт до вскрытия. */
function buildSeatCardFaceDown(seat, p, dealRevealed, showCardsArr, gameEnded) {
  return [0, 1, 2].map((ci) => {
    const dealt = dealRevealed?.[seat]?.[ci] ?? true;
    const showFace = shouldShowSeatCardFaces(seat, p, showCardsArr, gameEnded);
    return !dealt || !showFace;
  });
}

function buildDealCardKeys(seat, dealEpoch, dealRevealed) {
  if (!dealRevealed?.[seat]) return undefined;
  return [0, 1, 2].map(
    (ci) => `d${dealEpoch}-s${seat}-c${ci}-r${dealRevealed[seat][ci] ? 1 : 0}`
  );
}

/** Показ карты в слоте; до раздачи — плейсхолдер без рубашки. */
function buildShowCardByIndex(seat, dealRevealed) {
  if (!dealRevealed?.[seat]) return undefined;
  return [0, 1, 2].map((ci) => Boolean(dealRevealed[seat][ci]));
}

/** Какой seat показывает нижнюю полосу, если у героя нет карт на месте 0 (не опираемся на «свет» ботов). */
function getStripSeat(game) {
  if (!game?.players?.length) return 0;
  const withCards = game.players
    .map((pl, i) => (pl?.cards?.length > 0 ? i : -1))
    .filter((i) => i >= 0);
  if (withCards.includes(0)) return 0;
  return withCards.length > 0 ? Math.max(...withCards) : game.currentPlayer ?? 0;
}

/**
 * Бот: идти ли в раздачу с высоким анте (жёсткая свара ×N) или пропустить.
 * @returns {boolean} true = не платить анте (skipNextSvarkaAnte)
 */
function decideBotSkipSvarkaHighAnte(chips, effAnte) {
  const c = Number(chips) || 0;
  const e = Math.max(1, Number(effAnte) || 1);
  if (c <= e) return true;
  if (Math.random() < 0.5) {
    return Math.random() < 0.5;
  }
  const ratio = c / e;
  const playProb = Math.min(0.92, 0.2 + 0.8 * (1 - 1 / ratio));
  return Math.random() > playProb;
}

function createSeatCardSlotRefs() {
  return Array.from({ length: 4 }, () => [
    React.createRef(),
    React.createRef(),
    React.createRef(),
  ]);
}

function createSeatAnteSourceRefs() {
  return Array.from({ length: 4 }, () => React.createRef());
}

/** Сторона action-бейджа рядом с ником: смотрим в центр стола. */
const DEAL_FLIGHT_MS = 150;
/** Пауза между игроками после трёх карт одному месту. */
const DEAL_BETWEEN_PLAYERS_MS = 175;
/** Полёт одной карты в «пачке» игрока: на ~30% быстрее базы. */
const DEAL_FLIGHT_BURST_MS = Math.round(DEAL_FLIGHT_MS * 0.7);
/** Интервал между картами одного игрока (prefers-reduced-motion), мс. */
const DEAL_STAGGER_BURST_MS = 30;
/** Громкость одного «снятия» карты при серии раздач (основной множитель в play). */
const DEAL_CARD_SOUND_VOL = 0.78;

function botDecision(game, seat) {
  const p = game.players[seat];
  if (!p || p.isFolded) return { action: 'CHECK' };
  const neededToCall = Math.max(0, game.currentBet - p.currentBet);
  const step = game.config?.minRaise ?? 10;
  const minRaise = game.currentBet + step;
  const maxRaise = Math.min(p.chips * (p.isDark ? 2 : 1), game.currentBet + p.chips);
  const canRaise = minRaise <= maxRaise;
  const r = Math.random();

  if (p.isDark && r < 0.24) return { action: 'LOOK' };
  if (game.roundNumber >= 2 && neededToCall > 0 && r < 0.1) return { action: 'SHOWDOWN' };

  if (neededToCall > 0) {
    const cost = p.isDark ? Math.floor(neededToCall / 2) : neededToCall;
    if (cost > p.chips * 0.7 && r < 0.45) return { action: 'FOLD' };
    if (canRaise && r < 0.2) return { action: 'RAISE', amount: minRaise };
    return { action: 'CALL' };
  }

  if (canRaise && r < 0.18) return { action: 'RAISE', amount: minRaise };
  return { action: 'CHECK' };
}

function SeatLastMoveBadge({ action, isDimmed, compact = false }) {
  if (!action?.kind) return null;
  let Icon = Check;
  let short = '';
  let tone =
    'border-border/80 bg-muted/25 text-foreground/90 hover:border-neon-cyan/40 hover:bg-muted/45 hover:shadow-[0_0_12px_rgba(0,255,255,0.12)]';
  let iconTone = 'text-muted-foreground';

  switch (action.kind) {
    case 'fold':
      Icon = XCircle;
      short = 'Пас';
      tone = 'border-red-500/35 bg-red-950/25 text-red-100/95';
      iconTone = 'text-red-400/95';
      break;
    case 'look':
      Icon = Eye;
      short = 'Смотрит';
      tone = 'border-neon-gold/35 bg-amber-950/25 text-amber-100/95';
      iconTone = 'text-neon-gold';
      break;
    case 'call':
      Icon = Phone;
      short = 'Колл';
      tone = 'border-cyan-500/35 bg-cyan-950/20 text-cyan-50/95';
      iconTone = 'text-neon-cyan';
      break;
    case 'check':
      Icon = Check;
      short = 'Чек';
      tone = 'border-cyan-500/35 bg-cyan-950/20 text-cyan-50/95';
      iconTone = 'text-neon-cyan';
      break;
    case 'raise':
      Icon = TrendingUp;
      short = action.amount ? `Рейз ${action.amount}` : 'Рейз';
      tone = 'border-neon-orange/40 bg-orange-950/25 text-orange-50/95';
      iconTone = 'text-neon-orange';
      break;
    case 'showdown':
      Icon = Sparkles;
      short = 'Вскрытие';
      tone = 'border-violet-400/35 bg-violet-950/25 text-violet-100/95';
      iconTone = 'text-violet-300';
      break;
    default:
      short = action.kind;
  }

  return (
    <div
      className={cn(
        'flex max-w-[92px] cursor-default select-none items-center justify-center gap-1 rounded-md border',
        compact ? 'px-1.5 py-[1px] text-[9px]' : 'px-2 py-0.5 text-[10px]',
        'font-semibold leading-tight shadow-sm outline-none transition',
        tone,
        isDimmed && 'opacity-45'
      )}
      title={short}
    >
      <Icon className={cn('shrink-0 opacity-95', compact ? 'h-2.5 w-2.5' : 'h-3 w-3', iconTone)} aria-hidden />
      <span className="truncate">{short}</span>
    </div>
  );
}

function LocalCurrencyToast({ message, kind = 'info', onClose }) {
  const onCloseRef = useRef(onClose);
  const [leaving, setLeaving] = useState(false);
  const [coinImgMissing, setCoinImgMissing] = useState(false);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const leaveTimer = setTimeout(() => setLeaving(true), 2300);
    const closeTimer = setTimeout(() => onCloseRef.current?.(), 2700);
    return () => {
      clearTimeout(leaveTimer);
      clearTimeout(closeTimer);
    };
  }, []);

  const text = String(message || '');
  const deltaMatch = text.match(/([+-]\s?[\d\s.,]+)\s*SEKA/i);
  const isDebit = kind === 'debit';
  const isCredit = kind === 'credit';
  const isCurrencyToast = isDebit || isCredit;
  const shellClass = isDebit
    ? 'border-red-400/45 from-[#3a141a]/96 via-[#281018]/96 to-[#15080c]/96 shadow-[0_12px_34px_rgba(220,38,38,0.28)]'
    : isCredit
      ? 'border-emerald-300/55 from-[#10351f]/96 via-[#0f2b1b]/96 to-[#0a1a10]/96 shadow-[0_12px_34px_rgba(34,197,94,0.34)]'
      : 'border-cyan-300/45 from-[#10364d]/96 via-[#0e283b]/96 to-[#0a1726]/96 shadow-[0_12px_32px_rgba(34,211,238,0.26)]';
  const iconClass = isDebit
    ? 'border-red-300/35 bg-red-500/18 text-rose-100'
    : isCredit
      ? 'border-emerald-300/45 bg-emerald-500/20 text-yellow-200'
      : 'border-cyan-300/35 bg-cyan-500/15 text-cyan-100';
  const Icon = isCurrencyToast ? Coins : BadgeCheck;
  const coinImgSrc = `${process.env.PUBLIC_URL || ''}/1fishka.png`;
  const toneClass = isDebit ? 'text-rose-100' : isCredit ? 'text-yellow-200' : 'text-cyan-50';
  const shift = leaving ? '-translate-y-1 scale-[0.985] opacity-0' : 'translate-y-0 scale-100 opacity-100';

  return (
    <div className="pointer-events-none fixed left-1/2 top-[max(0.7rem,env(safe-area-inset-top,0px))] z-[130] w-auto max-w-[min(calc(100%-2.25rem),22rem)] -translate-x-1/2">
      <div
        className={cn(
          'relative overflow-hidden rounded-xl border bg-gradient-to-br px-3 py-2 backdrop-blur-md transition-all duration-300 ease-out',
          'animate-[app-toast-in_360ms_cubic-bezier(0.2,0.8,0.2,1)]',
          shellClass,
          shift
        )}
        role={isDebit ? 'alert' : 'status'}
      >
        <span className="pointer-events-none absolute inset-x-0 top-0 h-[40%] bg-gradient-to-b from-white/18 to-transparent" aria-hidden />
        <div className="relative z-[1] flex items-center gap-2.5">
          <span className={cn('inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border', iconClass)}>
            {isCurrencyToast ? (
              !coinImgMissing ? (
                <img
                  src={coinImgSrc}
                  alt=""
                  width={20}
                  height={20}
                  className="h-5 w-5 object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)]"
                  onError={() => setCoinImgMissing(true)}
                  draggable={false}
                  aria-hidden
                />
              ) : null
            ) : (
              <Icon className="h-4.5 w-4.5" aria-hidden />
            )}
          </span>
          <p className={cn('min-w-0 break-words leading-snug text-[0.95rem] font-black tracking-[0.02em]', toneClass)}>
            {deltaMatch ? (
              <>
                <span>{text.replace(deltaMatch[0], '').trim()} </span>
                <span
                  className={cn(
                    'drop-shadow-[0_0_8px_rgba(255,255,255,0.35)]',
                    isDebit ? 'text-red-300' : 'text-emerald-300 drop-shadow-[0_0_8px_rgba(74,222,128,0.6)]'
                  )}
                >
                  {deltaMatch[0]}
                </span>
              </>
            ) : (
              text
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

function LocalGameInner({ user, onBack, onUserPatch }) {
  const { play, playLoop, stopLoop, stopLoopBySound, initAudio } = useAudio();
  const heroName = useMemo(() => {
    const n = (user?.username || 'Игрок').trim();
    return n || 'Игрок';
  }, [user?.username]);
  const heroAvatarUrl = useMemo(() => {
    const u = user?.avatarUrl;
    return typeof u === 'string' && u.trim() ? u.trim() : '';
  }, [user?.avatarUrl]);
  const [game, setGame] = useState(new LocalSekaGame());
  const gameTimerSnapshotRef = useRef(game);
  gameTimerSnapshotRef.current = game;
  const [gameStarted, setGameStarted] = useState(false);
  const [botCount, setBotCount] = useState(1);
  const [showCards, setShowCards] = useState([false, false, false, false]);
  const [lastSeatActions, setLastSeatActions] = useState([null, null, null, null]);
  const [gameStats, setGameStats] = useState({
    heroWins: 0,
    botWins: 0,
    totalGames: 0,
  });
  const [tableStartingStack, setTableStartingStack] = useState(DEFAULT_SEKA_CONFIG.startingChips);
  const [tableAnte, setTableAnte] = useState(DEFAULT_SEKA_CONFIG.ante);
  /** Снимок стола для серверного cap при flush дельты (`App.js` → `POST /api/seka/local/wallet/delta`). */
  const localSekaWalletPatchMeta = useMemo(
    () => ({
      walletDeltaSource: 'local_seka',
      localSekaLimitsSnapshot: {
        botCount: Math.max(0, Math.floor(Number(botCount) || 0)),
        tableAnte: Math.max(0, Math.floor(Number(tableAnte) || 0)),
        startingStack: Math.max(1, Math.floor(Number(tableStartingStack) || 0)),
      },
    }),
    [botCount, tableAnte, tableStartingStack]
  );
  const [showRaiseInput, setShowRaiseInput] = useState(false);
  const [sekaRaiseAmount, setSekaRaiseAmount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showBuyInModal, setShowBuyInModal] = useState(false);
  const [buyInAmount, setBuyInAmount] = useState(0);
  const [heroOutNoticeCollapsed, setHeroOutNoticeCollapsed] = useState(false);
  /** Герой наблюдает за матчем без стека — не показывать снова модалку «недостаточно фишек» после каждой раздачи. */
  const [spectatorObservingMatch, setSpectatorObservingMatch] = useState(false);
  const losePanelSoundPlayedRef = useRef(false);
  const winPanelSoundPlayedRef = useRef(false);
  const outcomeSoundDelayRef = useRef(null);
  /** winner-game после закрытия SessionResultOverlay (не перекрывать money-SFX). */
  const winnerFanfareAfterStingerCloseRef = useRef(false);
  /** Жёсткая свара (×8): снимок до анте, таймер 5 с. */
  const svarkaSnapshotRef = useRef(null);
  const svarkaFxLoopRef = useRef(null);
  const svarkaCommitChoiceTimersRef = useRef([]);
  const svarkaCommitRevealTimerRef = useRef(null);
  const svarkaCommitResolvedRef = useRef({});
  const svarkaCommitRevealStartedRef = useRef(false);
  const svarkaCommitIntervalRef = useRef(null);
  const [svarkaCommitOpen, setSvarkaCommitOpen] = useState(false);
  const [svarkaCommitSeconds, setSvarkaCommitSeconds] = useState(SVARKA_COMMIT_SECONDS);
  const [svarkaCommitMeta, setSvarkaCommitMeta] = useState({ mult: 1, eff: 0 });
  const [svarkaCommitPhase, setSvarkaCommitPhase] = useState('collecting');
  const [svarkaCommitSeats, setSvarkaCommitSeats] = useState([]);
  const [showMatchHistoryModal, setShowMatchHistoryModal] = useState(false);
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  /** Развёрнута ли в модалке итога раздачи секция с хроникой, разбором и полной историей. */
  const [outcomeDetailsExpanded, setOutcomeDetailsExpanded] = useState(false);
  /** Универсальное окно начислений: закрыто после 3 с → показываем классическую модалку итога. */
  const [localStingerClosed, setLocalStingerClosed] = useState(false);
  /** Раскрыта ли под панелью действий живая лента ходов текущей раздачи. */
  const [liveMovesOpen, setLiveMovesOpen] = useState(false);
  const [historyFilter, setHistoryFilter] = useState('all');
  const [matchHistory, setMatchHistory] = useState([]);
  /** Пошаговая хроника: свары и раздачи по времени (для модалки «История ходов»). */
  const [matchChronicle, setMatchChronicle] = useState([]);
  const chronicleHandKeysRef = useRef(new Set());
  const timerLoopRef = useRef(null);
  const turnTimerRef = useRef(null);
  /** Актуальный handlePlayerMove для автопаса по таймеру (избегаем дублирования логики и гонок со stale state). */
  const handlePlayerMoveRef = useRef(null);
  /** Стек героя (место 0) в начале текущей раздачи — для строки ±SEKA в итоге. */
  const heroStackAtHandStartRef = useRef(null);
  /** Стеки всех мест в начале текущей раздачи — для строк ±SEKA в разборе. */
  const handStartStacksRef = useRef([]);
  /** Базовый стек героя на старте текущей локальной сессии (для net profit). */
  const sessionStartHeroStackRef = useRef(0);
  /** Защита от повторного начисления в рамках одной абсолютной победы. */
  const absolutePayoutAppliedRef = useRef(false);
  /** Кошелёк до начисления чистой прибыли (оверлей не должен показывать баланс дважды после onUserPatch). */
  const absoluteVictoryWalletForStingerRef = useRef(null);
  /** Сумма прибыли с «стола», уже зачисленная в кошелёк (абсолютная победа), чтобы при выходе не удваивать баланс. */
  const tableProfitCreditedToWalletRef = useRef(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [botTurnClock, setBotTurnClock] = useState(null);
  const [, setBotRingTick] = useState(0);
  /** Пошаговая раздача: [место][0..2] = карта «приземлилась». */
  const [dealRevealed, setDealRevealed] = useState(null);
  /** Версия раздачи для ключей анимации (синхронно с dealGenRef). */
  const [dealEpoch, setDealEpoch] = useState(0);
  const dealGenRef = useRef(0);
  const dealRevealedRef = useRef(null);
  const dealTimersRef = useRef([]);
  const deckRef = useRef(null);
  const seatCardSlotRefsRef = useRef(null);
  if (!seatCardSlotRefsRef.current) {
    seatCardSlotRefsRef.current = createSeatCardSlotRefs();
  }
  const heroStripSlotRefs = useRef([
    React.createRef(),
    React.createRef(),
    React.createRef(),
  ]);
  const dealFlightAnimsRef = useRef([]);
  const dealFlightRootsRef = useRef([]);
  /** Прерывание полёта (resize и т.п.); новая раздача гасит старый цикл через `dealGenRef`. */
  const dealFlightAbortRef = useRef(false);
  const [localAntePhaseText, setLocalAntePhaseText] = useState('');
  const [localAnteFlights, setLocalAnteFlights] = useState([]);
  const potAnchorRef = useRef(null);
  const anteSourceRefsRef = useRef(null);
  if (!anteSourceRefsRef.current) {
    anteSourceRefsRef.current = createSeatAnteSourceRefs();
  }

  const seatArenaRef = useRef(null);
  const seatDragRef = useRef(null);
  const [seatArrangeMode, setSeatArrangeMode] = useState(false);
  const [seatOffsetBySeat, setSeatOffsetBySeat] = useState({});
  const [seatProfileOpen, setSeatProfileOpen] = useState(null);

  const tagSeatAction = useCallback((seat, payload) => {
    if (seat == null || seat < 0) return;
    setLastSeatActions((prev) => {
      const next = [...prev];
      next[seat] = payload ? { ...payload, at: Date.now() } : null;
      return next;
    });
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const applyOutcomeStats = useCallback((resolvedGame) => {
    const heroWon =
      (resolvedGame.winner && resolvedGame.winner.id === 1) ||
      (!resolvedGame.winner && (resolvedGame.winners || []).some((w) => w.id === 1));
    setGameStats((prev) => ({
      ...prev,
      totalGames: prev.totalGames + 1,
      heroWins: heroWon ? prev.heroWins + 1 : prev.heroWins,
      botWins: heroWon ? prev.botWins : prev.botWins + 1,
    }));
  }, []);

  const stopHeroTimerLoop = useCallback(() => {
    if (timerLoopRef.current) {
      stopLoop(timerLoopRef.current);
      timerLoopRef.current = null;
      return;
    }
    stopLoopBySound('timer-start');
  }, [stopLoop, stopLoopBySound]);

  const ensureHeroTimerLoop = useCallback(() => {
    if (timerLoopRef.current) return;
    const loopId = playLoop('timer-start', 1);
    if (loopId) timerLoopRef.current = loopId;
  }, [playLoop]);

  const startTurnTimer = useCallback(() => {
    if (turnTimerRef.current) clearInterval(turnTimerRef.current);
    setTimeLeft(SEKA_HERO_TURN_SECONDS_CEIL);

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setTimeout(() => {
            const g = gameTimerSnapshotRef.current;
            const p = g?.players?.[0];
            if (!p || g?.gameEnded || g?.currentPlayer !== 0) return;
            const needed = Math.max(0, Number(g.currentBet || 0) - Number(p.currentBet || 0));
            handlePlayerMoveRef.current?.(needed <= 0 ? 'CHECK' : 'FOLD', 0, { onlyIfCurrentSeat: 0 });
          }, 0);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    turnTimerRef.current = timer;
  }, []);

  const stopTurnTimer = useCallback(() => {
    if (turnTimerRef.current) {
      clearInterval(turnTimerRef.current);
      turnTimerRef.current = null;
    }
    stopHeroTimerLoop();
    setTimeLeft(0);
  }, [stopHeroTimerLoop]);

  const abortDealFlights = useCallback(() => {
    dealFlightAnimsRef.current.forEach((a) => {
      try {
        a.cancel();
      } catch (_) {
        /* ignore */
      }
    });
    dealFlightAnimsRef.current = [];
    dealFlightRootsRef.current.forEach(({ root, el }) => {
      try {
        root.unmount();
      } catch (_) {
        /* ignore */
      }
      try {
        el.remove();
      } catch (_) {
        /* ignore */
      }
    });
    dealFlightRootsRef.current = [];
  }, []);

  const clearDealTimers = useCallback(() => {
    dealTimersRef.current.forEach((id) => clearTimeout(id));
    dealTimersRef.current = [];
    abortDealFlights();
    setLocalAntePhaseText('');
    setLocalAnteFlights([]);
  }, [abortDealFlights]);

  useEffect(() => () => clearDealTimers(), [clearDealTimers]);

  dealRevealedRef.current = dealRevealed;

  useEffect(() => {
    const onResize = () => {
      if (localAnteFlights.length > 0 || localAntePhaseText) {
        setLocalAntePhaseText('');
        setLocalAnteFlights([]);
      }
      const dr = dealRevealedRef.current;
      if (!dr || !dr.some((row) => row.some((v) => !v))) return;
      dealFlightAbortRef.current = true;
      abortDealFlights();
      dealTimersRef.current.forEach((id) => clearTimeout(id));
      dealTimersRef.current = [];
      setDealRevealed((prev) => (prev ? prev.map((row) => row.map(() => true)) : prev));
      dealFlightAbortRef.current = false;
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [abortDealFlights, localAnteFlights.length, localAntePhaseText]);

  /** Анте-анимация и раздача карт (общий путь для старта матча и продолжения после свара). */
  const scheduleAnteAndDeal = useCallback(
    (working, prep, gen, numSeats, anteBannerText) => {
      setGame(cloneLocalGame(working));
      setGameStarted(true);
      setShowCards(Array.from({ length: numSeats }, () => false));
      setLastSeatActions(Array.from({ length: numSeats }, () => null));
      setBotTurnClock(null);
      setShowRaiseInput(false);
      setDealRevealed(null);

      const phaseMs = 760;
      const staggerMs = 75;
      const contributions = [...(working.lastAnteContributions || [])]
        .filter((c) => Number(c?.amount || 0) > 0 && Number(c?.seatIndex) >= 0)
        .sort((a, b) => Number(a.seatIndex) - Number(b.seatIndex))
        .map((c, idx) => ({
          seatIndex: Number(c.seatIndex),
          amount: Number(c.amount),
          delayMs: idx * staggerMs,
          key: `local-ante-${gen}-${Number(c.seatIndex)}-${idx}`,
        }));

      setLocalAntePhaseText(anteBannerText);

      const prefersReducedAnte =
        typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const buildLocalAnteFlights = () => {
        const potRect = potAnchorRef.current?.getBoundingClientRect?.();
        const deckRect = deckRef.current?.getBoundingClientRect?.();
        const targetX = potRect
          ? potRect.left + potRect.width / 2
          : deckRect
            ? deckRect.left + deckRect.width / 2
            : window.innerWidth / 2;
        const targetY = potRect
          ? potRect.top + potRect.height / 2
          : deckRect
            ? deckRect.top + deckRect.height / 2
            : window.innerHeight / 2;
        const flights = contributions.map((entry, idx) => {
          const seat = Number(entry.seatIndex);
          const seatRect = anteSourceRefsRef.current?.[seat]?.current?.getBoundingClientRect?.();
          const startX = seatRect ? seatRect.left + seatRect.width / 2 : targetX;
          const startY = seatRect ? seatRect.top + seatRect.height / 2 : targetY;
          const dx = targetX - startX;
          const dy = targetY - startY;
          const spreadX = Math.round((Math.random() - 0.5) * 34);
          const spreadY = Math.round((Math.random() - 0.5) * 22);
          return {
            id: `${entry.key}-${idx}`,
            amount: Number(entry.amount || 0),
            delayMs: Number(entry.delayMs || 0),
            startX,
            startY,
            dx,
            dy,
            coinDx: dx + spreadX,
            coinDy: dy + spreadY,
          };
        });
        return {
          target: { x: targetX, y: targetY },
          flights,
        };
      };

      const runHoleDealAndFlights = () => {
        if (dealGenRef.current !== gen) return;
        working.dealHoleCardsToEligible(prep.eligiblePlayers);
        if (working.gameEnded) {
          applyOutcomeStats(working);
        }
        setGame(cloneLocalGame(working));
        setLocalAntePhaseText('');
        setLocalAnteFlights([]);

        const n = working.players.length;
        const mask = Array.from({ length: n }, () => [false, false, false]);
        setDealEpoch(gen);
        setDealRevealed(mask);

        const heroUsesStrip =
          working.players[0]?.cards?.length > 0 && !working.gameEnded;

        const prefersReducedCards =
          typeof window !== 'undefined' &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        const revealSeatCard = (s, c) => {
          setDealRevealed((prev) => {
            if (dealGenRef.current !== gen || dealFlightAbortRef.current || !prev) return prev;
            return prev.map((row, si) =>
              si === s ? row.map((v, ci) => (ci === c ? true : v)) : row
            );
          });
        };

        const runDealFlights = async () => {
          await new Promise((r) => {
            requestAnimationFrame(() => requestAnimationFrame(r));
          });
          if (dealGenRef.current !== gen || dealFlightAbortRef.current) return;

          for (let s = 0; s < n; s++) {
            for (let c = 0; c < 3; c++) {
              if (dealGenRef.current !== gen || dealFlightAbortRef.current) return;

              play('card-deal', DEAL_CARD_SOUND_VOL);

              const deckEl = deckRef.current;
              const targetRef =
                s === 0 && heroUsesStrip ? heroStripSlotRefs.current[c] : seatCardSlotRefsRef.current[s][c];
              const targetEl = targetRef?.current;
              const useLg = s === 0 && heroUsesStrip;

              if (!deckEl || !targetEl) {
                revealSeatCard(s, c);
                continue;
              }

              const deckRect = deckEl.getBoundingClientRect();
              let targetRect = targetEl.getBoundingClientRect();
              if (deckRect.width < 2 || targetRect.width < 2) {
                revealSeatCard(s, c);
                continue;
              }

              const wrapper = document.createElement('div');
              wrapper.style.position = 'fixed';
              wrapper.style.left = `${deckRect.left}px`;
              wrapper.style.top = `${deckRect.top}px`;
              wrapper.style.width = `${deckRect.width}px`;
              wrapper.style.height = `${deckRect.height}px`;
              wrapper.style.zIndex = '25';
              wrapper.style.pointerEvents = 'none';
              wrapper.style.transformOrigin = 'top left';
              document.body.appendChild(wrapper);

              const root = createRoot(wrapper);
              root.render(<PlayingCard faceDown size={useLg ? 'lg' : 'sm'} />);
              dealFlightRootsRef.current.push({ root, el: wrapper });

              await new Promise((resolve) => {
                requestAnimationFrame(() => {
                  if (dealGenRef.current !== gen || dealFlightAbortRef.current) {
                    try {
                      root.unmount();
                    } catch (_) {
                      /* ignore */
                    }
                    try {
                      wrapper.remove();
                    } catch (_) {
                      /* ignore */
                    }
                    dealFlightRootsRef.current = dealFlightRootsRef.current.filter((x) => x.el !== wrapper);
                    revealSeatCard(s, c);
                    resolve();
                    return;
                  }

                  targetRect = targetEl.getBoundingClientRect();
                  const wRect = wrapper.getBoundingClientRect();
                  const tx = targetRect.left - wRect.left;
                  const ty = targetRect.top - wRect.top;
                  const sx = targetRect.width / Math.max(wRect.width, 1);
                  const sy = targetRect.height / Math.max(wRect.height, 1);

                  const anim = wrapper.animate(
                    [
                      { transform: 'translate(0px, 0px) scale(1, 1)' },
                      { transform: `translate(${tx}px, ${ty}px) scale(${sx}, ${sy})` },
                    ],
                    {
                      duration: DEAL_FLIGHT_BURST_MS,
                      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
                      fill: 'forwards',
                    }
                  );
                  dealFlightAnimsRef.current.push(anim);

                  anim.finished
                    .catch(() => {})
                    .finally(() => {
                      dealFlightAnimsRef.current = dealFlightAnimsRef.current.filter((x) => x !== anim);
                      try {
                        root.unmount();
                      } catch (_) {
                        /* ignore */
                      }
                      try {
                        wrapper.remove();
                      } catch (_) {
                        /* ignore */
                      }
                      dealFlightRootsRef.current = dealFlightRootsRef.current.filter((x) => x.el !== wrapper);
                      if (dealGenRef.current !== gen || dealFlightAbortRef.current) {
                        resolve();
                        return;
                      }
                      revealSeatCard(s, c);
                      resolve();
                    });
                });
              });
            }
            if (s < n - 1) {
              if (dealGenRef.current !== gen || dealFlightAbortRef.current) return;
              await new Promise((resolve) => {
                setTimeout(resolve, DEAL_BETWEEN_PLAYERS_MS);
              });
              if (dealGenRef.current !== gen || dealFlightAbortRef.current) return;
            }
          }
        };

        if (prefersReducedCards) {
          for (let s = 0; s < n; s += 1) {
            for (let c = 0; c < 3; c += 1) {
              const delay =
                s * (3 * DEAL_STAGGER_BURST_MS + DEAL_BETWEEN_PLAYERS_MS) +
                c * DEAL_STAGGER_BURST_MS;
              const id = setTimeout(() => {
                play('card-deal', DEAL_CARD_SOUND_VOL);
                setDealRevealed((prev) => {
                  if (dealGenRef.current !== gen) return prev;
                  if (!prev) return prev;
                  const next = prev.map((row, si) =>
                    si === s ? row.map((v, ci) => (ci === c ? true : v)) : row
                  );
                  return next;
                });
              }, delay);
              dealTimersRef.current.push(id);
            }
          }
        } else {
          const startId = setTimeout(() => {
            void runDealFlights();
          }, 0);
          dealTimersRef.current.push(startId);
        }
      };

      if (prefersReducedAnte) {
        play('ante-coin', 0.92);
        setLocalAnteFlights([]);
        const id = setTimeout(() => {
          runHoleDealAndFlights();
        }, phaseMs);
        dealTimersRef.current.push(id);
      } else {
        const showBurstId = setTimeout(() => {
          play('ante-coin', 0.92);
          const flightData = buildLocalAnteFlights();
          setLocalAnteFlights(flightData.flights);
        }, phaseMs);
        dealTimersRef.current.push(showBurstId);
        const maxDelay =
          contributions.length > 0 ? contributions[contributions.length - 1].delayMs : 0;
        const clearId = setTimeout(() => {
          setLocalAntePhaseText('');
          runHoleDealAndFlights();
        }, phaseMs + maxDelay + 680);
        dealTimersRef.current.push(clearId);
      }
    },
    [applyOutcomeStats, play]
  );

  const startSvarkaContinuation = useCallback(
    (snapshot) => {
      clearDealTimers();
      stopTurnTimer();
      const working = cloneLocalGame(snapshot);
      const prep = working.prepareNewHand();
      if (!prep.canDealHole) {
        // keep previous hand-start baseline so winner delta reflects carried pot
        if (working.gameEnded) {
          applyOutcomeStats(working);
        }
        setDealRevealed(null);
        setGame(cloneLocalGame(working));
        setGameStarted(true);
        setShowCards(Array.from({ length: working.players.length }, () => false));
        setLastSeatActions(Array.from({ length: working.players.length }, () => null));
        setBotTurnClock(null);
        setShowRaiseInput(false);
        setLocalAntePhaseText('');
        setLocalAnteFlights([]);
        return;
      }

      working.collectAnteFromEligible(prep.eligiblePlayers);
      heroStackAtHandStartRef.current = working.players[0]?.chips ?? 0;
      handStartStacksRef.current = working.players.map((p) => (p ? p.chips : null));

      dealGenRef.current += 1;
      const gen = dealGenRef.current;

      const mult = Math.max(1, Number(working.svarkaAnteMultiplier) || 1);
      const eff = working.getEffectiveAnteAmount();
      const anteBannerText = buildLocalAntePhaseBannerText(mult, eff, prep.eligiblePlayers);

      scheduleAnteAndDeal(working, prep, gen, working.players.length, anteBannerText);
    },
    [applyOutcomeStats, clearDealTimers, scheduleAnteAndDeal, stopTurnTimer]
  );

  const applySvarkaParticipationAndDeal = useCallback(
    (snapshot, heroParticipates, participationBySeat = null) => {
      const working = cloneLocalGame(snapshot);
      const eff = working.getEffectiveAnteAmount();
      working.players.forEach((p, i) => {
        const explicit = participationBySeat && Object.prototype.hasOwnProperty.call(participationBySeat, i)
          ? participationBySeat[i]
          : undefined;
        if (i === 0) {
          const heroChoice = typeof explicit === 'boolean' ? explicit : heroParticipates;
          p.skipNextSvarkaAnte = !heroChoice;
        } else {
          if (typeof explicit === 'boolean') {
            p.skipNextSvarkaAnte = !explicit;
          } else {
            p.skipNextSvarkaAnte = decideBotSkipSvarkaHighAnte(p.chips, eff);
          }
        }
      });
      startSvarkaContinuation(working);
    },
    [startSvarkaContinuation]
  );

  const clearSvarkaCommitChoiceTimers = useCallback(() => {
    svarkaCommitChoiceTimersRef.current.forEach((id) => clearTimeout(id));
    svarkaCommitChoiceTimersRef.current = [];
    if (svarkaCommitRevealTimerRef.current) {
      clearTimeout(svarkaCommitRevealTimerRef.current);
      svarkaCommitRevealTimerRef.current = null;
    }
  }, []);

  const clearSvarkaCommitTimer = useCallback(() => {
    if (svarkaCommitIntervalRef.current) {
      clearInterval(svarkaCommitIntervalRef.current);
      svarkaCommitIntervalRef.current = null;
    }
  }, []);

  const revealSvarkaCommitChoices = useCallback(
    (force = false) => {
      if (!svarkaCommitOpen || svarkaCommitRevealStartedRef.current) return;
      const seats = Array.isArray(svarkaCommitSeats) ? svarkaCommitSeats : [];
      if (seats.length === 0) return;
      const allChosen = seats.every((s) => s.choiceMade);
      if (!force && !allChosen) return;

      clearSvarkaCommitTimer();
      clearSvarkaCommitChoiceTimers();
      svarkaCommitRevealStartedRef.current = true;
      const resolved = { ...(svarkaCommitResolvedRef.current || {}) };
      const revealSeats = seats.map((seat) => {
        const can = Boolean(seat.canParticipate);
        let participate = resolved[seat.seatIndex];
        if (typeof participate !== 'boolean') {
          participate = false;
          resolved[seat.seatIndex] = false;
        }
        if (!can) {
          participate = false;
          resolved[seat.seatIndex] = false;
        }
        return {
          ...seat,
          choiceMade: true,
          participateRevealed: participate,
        };
      });

      svarkaCommitResolvedRef.current = resolved;
      setSvarkaCommitPhase('reveal');
      setSvarkaCommitSeats(revealSeats);
      setSvarkaCommitSeconds(0);

      const snap = svarkaSnapshotRef.current ? cloneLocalGame(svarkaSnapshotRef.current) : null;
      svarkaCommitRevealTimerRef.current = setTimeout(() => {
        const outcome = { ...(svarkaCommitResolvedRef.current || {}) };
        if (snap?.players?.length) {
          const mult = Math.max(1, Number(snap.svarkaAnteMultiplier) || 1);
          const eff = typeof snap.getEffectiveAnteAmount === 'function' ? snap.getEffectiveAnteAmount() : 0;
          const steps = [
            `Свара ×${mult}`,
            `Номинал анте: ${Number(eff).toLocaleString('ru-RU')} SEKA`,
            ...snap.players.map((p, i) => describeSvarkaSeatChoice(p, i, outcome[i], eff)),
            'Далее — списание анте и раздача карт',
          ];
          setMatchChronicle((prev) => [
            ...prev,
            {
              id: `svarka-commit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              ts: Date.now(),
              kind: 'svarka',
              title: `Свара ×${mult} — голосование`,
              steps,
            },
          ]);
        }
        svarkaSnapshotRef.current = null;
        svarkaCommitResolvedRef.current = {};
        svarkaCommitRevealStartedRef.current = false;
        setSvarkaCommitOpen(false);
        setSvarkaCommitPhase('collecting');
        setSvarkaCommitSeats([]);
        if (snap) {
          applySvarkaParticipationAndDeal(snap, Boolean(outcome[0]), outcome);
        }
      }, 1050);
    },
    [
      applySvarkaParticipationAndDeal,
      clearSvarkaCommitChoiceTimers,
      clearSvarkaCommitTimer,
      svarkaCommitOpen,
      svarkaCommitSeats,
    ]
  );

  const queueLocalSvarkaBlindChoices = useCallback(
    (snapshot) => {
      clearSvarkaCommitChoiceTimers();
      svarkaCommitRevealStartedRef.current = false;
      // Сбрасываем таймер до открытия модалки, чтобы полоса сразу стартовала с полного значения.
      setSvarkaCommitSeconds(SVARKA_COMMIT_SECONDS);
      const working = cloneLocalGame(snapshot);
      const eff = working.getEffectiveAnteAmount();
      const resolved = {};
      const seats = working.players.map((p, i) => {
        const canParticipate = !Boolean(p.isSittingOut) && Number(p.chips || 0) > 0;
        if (!canParticipate) {
          resolved[i] = false;
        } else if (i !== 0) {
          resolved[i] = !decideBotSkipSvarkaHighAnte(p.chips, eff);
        }
        const chipN = Number(p.chips || 0);
        const entryAmount = Math.min(eff, chipN);
        return {
          seatIndex: i,
          name: p.name,
          isBot: i !== 0,
          chips: chipN,
          entryAmount,
          canParticipate,
          choiceMade: !canParticipate,
          participateRevealed: null,
        };
      });

      svarkaCommitResolvedRef.current = resolved;
      setSvarkaCommitPhase('collecting');
      setSvarkaCommitSeats(seats);

      seats
        .filter((s) => s.isBot && s.canParticipate)
        .forEach((seat) => {
          const delay = 450 + Math.floor(Math.random() * 2100);
          const id = setTimeout(() => {
            setSvarkaCommitSeats((prev) =>
              prev.map((p) =>
                p.seatIndex === seat.seatIndex
                  ? {
                      ...p,
                      choiceMade: true,
                    }
                  : p
              )
            );
          }, delay);
          svarkaCommitChoiceTimersRef.current.push(id);
        });
    },
    [clearSvarkaCommitChoiceTimers]
  );

  useEffect(() => {
    if (!svarkaCommitOpen || svarkaCommitPhase !== 'collecting') return;
    if (svarkaCommitSeats.length === 0) return;
    const allChosen = svarkaCommitSeats.every((s) => s.choiceMade);
    if (allChosen) {
      revealSvarkaCommitChoices(false);
    }
  }, [revealSvarkaCommitChoices, svarkaCommitOpen, svarkaCommitPhase, svarkaCommitSeats]);

  useEffect(() => {
    if (!svarkaCommitOpen) return undefined;
    setSvarkaCommitSeconds(SVARKA_COMMIT_SECONDS);
    let s = SVARKA_COMMIT_SECONDS;
    const id = setInterval(() => {
      s -= 1;
      setSvarkaCommitSeconds(s);
      if (s === 0) {
        clearInterval(id);
        svarkaCommitIntervalRef.current = null;
        if (!Object.prototype.hasOwnProperty.call(svarkaCommitResolvedRef.current || {}, 0)) {
          svarkaCommitResolvedRef.current = {
            ...(svarkaCommitResolvedRef.current || {}),
            0: false,
          };
        }
        setSvarkaCommitSeats((prev) =>
          prev.map((seat) =>
            seat.seatIndex === 0
              ? {
                  ...seat,
                  choiceMade: true,
                }
              : seat
          )
        );
        revealSvarkaCommitChoices(true);
      }
    }, 1000);
    svarkaCommitIntervalRef.current = id;
    return () => {
      clearInterval(id);
      svarkaCommitIntervalRef.current = null;
    };
  }, [svarkaCommitOpen, revealSvarkaCommitChoices]);

  useEffect(() => {
    if (!svarkaCommitOpen) {
      clearSvarkaCommitChoiceTimers();
      svarkaCommitRevealStartedRef.current = false;
      svarkaCommitResolvedRef.current = {};
      if (svarkaFxLoopRef.current) {
        stopLoop(svarkaFxLoopRef.current);
        svarkaFxLoopRef.current = null;
      }
      stopLoopBySound('svarka-x8');
      return;
    }
    if (!svarkaFxLoopRef.current) {
      const loopId = playLoop('svarka-x8', 0.95);
      if (loopId) {
        svarkaFxLoopRef.current = loopId;
      } else {
        play('svarka-x8', 0.98);
      }
    }
    return () => {
      if (svarkaFxLoopRef.current) {
        stopLoop(svarkaFxLoopRef.current);
        svarkaFxLoopRef.current = null;
      }
      stopLoopBySound('svarka-x8');
    };
  }, [clearSvarkaCommitChoiceTimers, play, playLoop, stopLoop, stopLoopBySound, svarkaCommitOpen]);

  const onSvarkaCommitParticipate = useCallback(() => {
    if (svarkaCommitPhase !== 'collecting') return;
    const heroSeat = svarkaCommitSeats.find((s) => s.seatIndex === 0);
    if (!heroSeat?.canParticipate) return;
    setSvarkaCommitSeats((prev) =>
      prev.map((seat) =>
        seat.seatIndex === 0
          ? {
              ...seat,
              choiceMade: true,
            }
          : seat
      )
    );
    svarkaCommitResolvedRef.current = {
      ...(svarkaCommitResolvedRef.current || {}),
      0: true,
    };
  }, [svarkaCommitPhase, svarkaCommitSeats]);

  const onSvarkaCommitSkip = useCallback(() => {
    if (svarkaCommitPhase !== 'collecting') return;
    const heroSeat = svarkaCommitSeats.find((s) => s.seatIndex === 0);
    if (!heroSeat?.canParticipate) return;
    setSvarkaCommitSeats((prev) =>
      prev.map((seat) =>
        seat.seatIndex === 0
          ? {
              ...seat,
              choiceMade: true,
            }
          : seat
      )
    );
    svarkaCommitResolvedRef.current = {
      ...(svarkaCommitResolvedRef.current || {}),
      0: false,
    };
  }, [svarkaCommitPhase, svarkaCommitSeats]);

  const startGame = useCallback((preserveStacks) => {
    clearDealTimers();
    stopTurnTimer();
    const seeds = buildPlayerSeeds(heroName.trim() || 'Игрок', botCount);
    const currentBalance = Math.max(0, Math.floor(Number(user?.balance ?? 0)));
    const startStack = Math.max(1, Math.floor(Number(tableStartingStack) || DEFAULT_SEKA_CONFIG.startingChips));
    const selectedAnte = Math.max(1, Math.floor(Number(tableAnte) || DEFAULT_SEKA_CONFIG.ante));
    const validConfig = startStack > selectedAnte;
    if (!validConfig) return;
    if (!preserveStacks && currentBalance < startStack) return;
    const gameConfig = {
      startingChips: startStack,
      ante: selectedAnte,
      minRaise: Math.max(1, selectedAnte),
    };
    const newGame = new LocalSekaGame(gameConfig, seeds);
    if (preserveStacks) {
      newGame.gameHistory = [...matchHistory];
    }
    const sameShape = game.players.length === seeds.length;
    if (preserveStacks && gameStarted && game?.gameEnded && sameShape) {
      newGame.dealerIndex = (game.dealerIndex + 1) % newGame.players.length;
      newGame.players = newGame.players.map((p, i) => ({
        ...p,
        chips: game.players[i]?.chips ?? p.chips,
      }));
    }
    const working = newGame;
    if (!preserveStacks) {
      setSpectatorObservingMatch(false);
      setMatchHistory([]);
      setMatchChronicle([]);
      chronicleHandKeysRef.current = new Set();
      sessionStartHeroStackRef.current = Number(working.config?.startingChips ?? startStack);
      absolutePayoutAppliedRef.current = false;
      tableProfitCreditedToWalletRef.current = 0;
      onUserPatch?.({ ...localSekaWalletPatchMeta, balance: currentBalance - startStack });
      setNotifications((prev) => [
        ...prev,
        {
          id: `stack-deduct-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          message: `Вход в матч: -${startStack.toLocaleString('ru-RU')} SEKA`,
          kind: 'debit',
        },
      ]);
    }

    dealGenRef.current += 1;
    const gen = dealGenRef.current;

    const prep = working.prepareNewHand();
    if (!prep.canDealHole) {
      heroStackAtHandStartRef.current = working.players[0]?.chips ?? 0;
      handStartStacksRef.current = working.players.map((p) => (p ? p.chips : null));
      setDealRevealed(null);
      setGame(cloneLocalGame(working));
      setGameStarted(true);
      setShowCards(Array.from({ length: seeds.length }, () => false));
      setLastSeatActions(Array.from({ length: seeds.length }, () => null));
      setBotTurnClock(null);
      setShowRaiseInput(false);
      setLocalAntePhaseText('');
      setLocalAnteFlights([]);
      return;
    }

    working.collectAnteFromEligible(prep.eligiblePlayers);
    heroStackAtHandStartRef.current = working.players[0]?.chips ?? 0;
    handStartStacksRef.current = working.players.map((p) => (p ? p.chips : null));

    const mult = Math.max(1, Number(working.svarkaAnteMultiplier) || 1);
    const eff = working.getEffectiveAnteAmount();
    const anteBannerText = buildLocalAntePhaseBannerText(mult, eff, prep.eligiblePlayers);

    scheduleAnteAndDeal(working, prep, gen, seeds.length, anteBannerText);
  }, [
    botCount,
    clearDealTimers,
    game,
    gameStarted,
    heroName,
    localSekaWalletPatchMeta,
    matchHistory,
    onUserPatch,
    scheduleAnteAndDeal,
    stopTurnTimer,
    tableAnte,
    tableStartingStack,
    user?.balance,
  ]);

  const startNextHand = useCallback(() => {
    startGame(true);
  }, [startGame]);

  const handleStartNextHandClick = useCallback(() => {
    play('restart-game-button', 0.95);
    setOutcomeDetailsExpanded(false);
    startNextHand();
  }, [play, startNextHand]);

  /** После вылета со стека: наблюдение без повторного блокирующего окна про анте. */
  const handleContinueWatchingAfterBust = useCallback(() => {
    play('restart-game-button', 0.95);
    setSpectatorObservingMatch(true);
    setHeroOutNoticeCollapsed(true);
    setOutcomeDetailsExpanded(false);
    startNextHand();
  }, [play, startNextHand]);

  useEffect(() => {
    if (!game.gameEnded) {
      setOutcomeDetailsExpanded(false);
      setLiveMovesOpen(false);
      absoluteVictoryWalletForStingerRef.current = null;
    }
  }, [game.gameEnded]);

  const startFreshSession = useCallback(() => {
    startGame(false);
  }, [startGame]);

  const handleConfirmLeaveSession = useCallback(() => {
    stopTurnTimer();
    clearDealTimers();
    clearSvarkaCommitChoiceTimers();
    clearSvarkaCommitTimer();

    absolutePayoutAppliedRef.current = true;

    const snap = gameTimerSnapshotRef.current;
    let resolved = cloneLocalGame(snap);
    if (!resolved.gameEnded && resolved.gamePhase !== 'svarkaBetween') {
      resolved = fastForwardLocalGameToEnded(resolved);
    }

    const finalHero = Math.max(0, Math.floor(Number(resolved.players?.[0]?.chips ?? 0)));
    const alreadyCredited = Math.max(0, Math.floor(Number(tableProfitCreditedToWalletRef.current || 0)));
    const creditFromTable = Math.max(0, finalHero - alreadyCredited);
    const walletBefore = Math.max(0, Math.floor(Number(user?.balance ?? 0)));
    onUserPatch?.({ ...localSekaWalletPatchMeta, balance: walletBefore + creditFromTable });

    setLeaveModalOpen(false);
    setSvarkaCommitOpen(false);
    setSvarkaCommitPhase('collecting');
    setSvarkaCommitSeats([]);
    svarkaSnapshotRef.current = null;
    svarkaCommitResolvedRef.current = {};
    svarkaCommitRevealStartedRef.current = false;

    setGame(new LocalSekaGame());
    setGameStarted(false);
    setShowCards([false, false, false, false]);
    setLastSeatActions([null, null, null, null]);
    setBotTurnClock(null);
    setShowRaiseInput(false);
    setMatchHistory([]);
    setMatchChronicle([]);
    chronicleHandKeysRef.current = new Set();
    setDealRevealed(null);
    setLocalAntePhaseText('');
    setLocalAnteFlights([]);

    absolutePayoutAppliedRef.current = false;
    tableProfitCreditedToWalletRef.current = 0;
    sessionStartHeroStackRef.current = 0;

    onBack();
  }, [
    clearDealTimers,
    clearSvarkaCommitChoiceTimers,
    clearSvarkaCommitTimer,
    localSekaWalletPatchMeta,
    onBack,
    onUserPatch,
    stopTurnTimer,
    user?.balance,
  ]);

  const handleHeaderLeave = useCallback(() => {
    if (!gameStarted) {
      onBack();
      return;
    }
    if (svarkaCommitOpen) {
      setNotifications((prev) => [
        ...prev,
        {
          id: `leave-svarka-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          message: 'Закончите выбор по сварке — затем можно покинуть стол.',
          kind: 'info',
        },
      ]);
      return;
    }
    if (game.gamePhase === 'svarkaBetween') {
      setNotifications((prev) => [
        ...prev,
        {
          id: `leave-svarka-phase-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          message: 'Подождите окончания этапа свара или следующей раздачи.',
          kind: 'info',
        },
      ]);
      return;
    }
    setLeaveModalOpen(true);
  }, [game.gamePhase, gameStarted, onBack, svarkaCommitOpen]);

  const handlePlayerMove = useCallback((action, amount = 0, opts = {}) => {
    const onlyIfCurrentSeat = opts.onlyIfCurrentSeat;
    const actionKindMap = {
      LOOK: 'look',
      FOLD: 'fold',
      CALL: 'call',
      CHECK: 'check',
      RAISE: 'raise',
      SHOWDOWN: 'showdown',
    };

    setGame((prev) => {
      if (!prev || prev.gameEnded) return prev;
      if (onlyIfCurrentSeat != null && prev.currentPlayer !== onlyIfCurrentSeat) return prev;

      const upper = String(action || '').toUpperCase();
      const allowed = buildAllowedActionsForLocalCurrentTurn(prev);
      if (!allowed.some((a) => String(a?.action || '').toUpperCase() === upper)) return prev;

      const actingSeat = prev.currentPlayer;
      const prevPot = Number(prev.pot || 0);

      if (action === 'LOOK') {
        setShowCards((scPrev) => {
          const next = [...scPrev];
          if (actingSeat === 0) next[0] = true;
          return next;
        });
      }

      const newGame = cloneLocalGame(prev);
      const gameEnded = newGame.makeMove(actingSeat, action, amount);
      const nextPot = Number(newGame.pot || 0);
      const isPotBetAction = action === 'CALL' || action === 'RAISE';
      if (isPotBetAction && nextPot > prevPot) {
        play('allin-push-chips', 0.9);
      }
      if (actingSeat === 0) {
        stopTurnTimer();
      }
      tagSeatAction(actingSeat, {
        kind: actionKindMap[action] || String(action || '').toLowerCase(),
        amount: amount || undefined,
      });

      if (gameEnded) {
        stopTurnTimer();
        applyOutcomeStats(newGame);
      }

      if (newGame.gamePhase === 'svarkaBetween') {
        const mult = Math.max(1, Number(newGame.svarkaAnteMultiplier) || 1);
        const optInFrom = Number(
          newGame.config?.svarkaOptInFromMultiplier ?? DEFAULT_SEKA_CONFIG.svarkaOptInFromMultiplier
        );
        if (mult >= optInFrom) {
          const snap = cloneLocalGame(newGame);
          svarkaSnapshotRef.current = snap;
          setSvarkaCommitMeta({
            mult,
            eff: newGame.getEffectiveAnteAmount(),
          });
          queueLocalSvarkaBlindChoices(snap);
          setSvarkaCommitOpen(true);
        } else {
          if (mult === 2 || mult === 4 || mult === 8) {
            // Запускаем эффект в тот же тик, когда показываем баннер инициализации свары ×N.
            const svarkaInitSound = mult === 8 ? 'svarka-x8-init' : mult === 4 ? 'svarka-x4' : 'svarka-x2';
            play(svarkaInitSound, 1);
          }
          setNotifications((nPrev) => [
            ...nPrev,
            {
              id: `svarka-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              message: `Пустой круг — свара. Следующее анте ×${mult}.`,
              kind: 'info',
            },
          ]);
          setMatchChronicle((prev) => [
            ...prev,
            {
              id: `svarka-auto-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              ts: Date.now(),
              kind: 'svarka_auto',
              title: `Свара без голосования (×${mult})`,
              steps: [
                'Пустой первый круг — все чек',
                `Следующее анте ×${mult}`,
                'Далее — новая раздача',
              ],
            },
          ]);
          queueMicrotask(() => startSvarkaContinuation(cloneLocalGame(newGame)));
        }
      }

      return newGame;
    });
    setShowRaiseInput(false);
  }, [
    applyOutcomeStats,
    play,
    queueLocalSvarkaBlindChoices,
    startSvarkaContinuation,
    stopTurnTimer,
    tagSeatAction,
  ]);

  handlePlayerMoveRef.current = handlePlayerMove;

  /**
   * До dealHoleCards в движке currentPlayer остаётся 0 из конструктора; первый ход задаётся после раздачи.
   * Ждём ещё окончание анимации полёта карт (dealRevealed), иначе звук таймера включался бы на анте/раздаче.
   */
  const isHoleDealReadyForBetting = useMemo(() => {
    if (game.gamePhase === 'svarkaBetween') return false;
    if (!gameStarted || game.gameEnded) return true;
    if (dealRevealed == null) return false;
    return game.players.every((p, seatIdx) => {
      if (!p || !p.cards?.length) return true;
      const row = dealRevealed[seatIdx];
      if (!row || row.length !== 3) return false;
      return row.every(Boolean);
    });
  }, [dealRevealed, game.gameEnded, game.gamePhase, game.players, gameStarted]);

  /** После LOOK ход остаётся у героя — нужен для перезапуска таймера. */
  const heroDarkFlag = game.players?.[0]?.isDark;

  useEffect(() => {
    if (!gameStarted || game.gameEnded) {
      stopTurnTimer();
      return undefined;
    }
    if (game.gamePhase === 'svarkaBetween') {
      stopTurnTimer();
      return undefined;
    }
    if (game.currentPlayer !== 0) {
      stopTurnTimer();
      return undefined;
    }
    if (!isHoleDealReadyForBetting) {
      stopTurnTimer();
      return undefined;
    }
    startTurnTimer();
    ensureHeroTimerLoop();
    return () => stopTurnTimer();
  }, [
    ensureHeroTimerLoop,
    gameStarted,
    game.gameEnded,
    game.gamePhase,
    game.currentPlayer,
    heroDarkFlag,
    isHoleDealReadyForBetting,
    startTurnTimer,
    stopTurnTimer,
  ]);

  useEffect(() => {
    if (!gameStarted || game.gameEnded) return undefined;
    if (game.gamePhase === 'svarkaBetween') return undefined;
    if (game.currentPlayer === 0) return undefined;
    const seat = game.currentPlayer;
    const delay = 650 + Math.floor(Math.random() * 550);
    const endAt = Date.now() + delay;
    setBotTurnClock({ seat, endAt, totalMs: delay });
    const ringIv = setInterval(() => setBotRingTick((x) => x + 1), 50);
    const id = setTimeout(() => {
      const move = botDecision(game, seat);
      handlePlayerMove(move.action, move.amount || 0);
    }, delay);
    return () => {
      clearTimeout(id);
      clearInterval(ringIv);
      setBotTurnClock(null);
    };
  }, [game, game.gamePhase, gameStarted, handlePlayerMove]);

  const sekaBarActions = useMemo(() => {
    if (!gameStarted || game.gameEnded) return [];
    if (game.gamePhase === 'svarkaBetween') return [];
    if (game.currentPlayer !== 0) return [];
    return buildAllowedActionsForLocalCurrentTurn(game);
  }, [game, gameStarted]);

  const raiseRange = useMemo(() => {
    if (!gameStarted || game.gameEnded || !showRaiseInput) return null;
    const p = game.players[game.currentPlayer];
    const step = game.config?.minRaise ?? 10;
    const minRaise = game.currentBet + step;
    const maxRaise = Math.min(p.chips * (p.isDark ? 2 : 1), game.currentBet + p.chips);
    if (minRaise > maxRaise) return null;
    return { min: minRaise, max: maxRaise };
  }, [game, gameStarted, showRaiseInput]);

  const clampedSekaRaise = useMemo(() => {
    if (!raiseRange) return null;
    return Math.min(raiseRange.max, Math.max(raiseRange.min, sekaRaiseAmount));
  }, [raiseRange, sekaRaiseAmount]);

  /** Локальная секa: крупная рука снизу — всегда герой (0), иначе при «все в темную» strip шёл бы на currentPlayer и дублировал мелкие карты на месте. */
  const stripSeatUI = useMemo(() => {
    if (
      gameStarted &&
      game.players[0]?.cards?.length > 0 &&
      !game.gameEnded
    ) {
      return 0;
    }
    return getStripSeat(game);
  }, [game, gameStarted]);

  const onBarAction = (action) => {
    if (game.currentPlayer !== 0 || game.gameEnded) return;
    const allowed = buildAllowedActionsForLocalCurrentTurn(game);
    const actionUpper = String(action || '').toUpperCase();
    if (!allowed.some((a) => String(a?.action || '').toUpperCase() === actionUpper)) return;
    if (action === 'RAISE') {
      const p = game.players[game.currentPlayer];
      const step = game.config?.minRaise ?? 10;
      const minR = game.currentBet + step;
      const maxR = Math.min(p.chips * (p.isDark ? 2 : 1), game.currentBet + p.chips);
      setSekaRaiseAmount(Math.min(maxR, Math.max(minR, minR)));
      setShowRaiseInput(true);
      return;
    }
    handlePlayerMove(action);
  };

  const ante = Number(game.config?.ante ?? 10) || 10;
  /** Эффективное анте следующей раздачи (с учётом свара ×N). */
  const effAnteNext =
    typeof game.getEffectiveAnteAmount === 'function'
      ? Math.max(1, Math.floor(Number(game.getEffectiveAnteAmount()) || 0))
      : ante;
  const heroPlayer = game.players?.[0] || null;
  const heroTableChips = Number(heroPlayer?.chips ?? 0);
  const userBalance = Math.max(0, Math.floor(Number(user?.balance ?? 0)));
  const setupStartingStack = Math.max(1, Math.floor(Number(tableStartingStack) || DEFAULT_SEKA_CONFIG.startingChips));
  const setupAnte = Math.max(1, Math.floor(Number(tableAnte) || DEFAULT_SEKA_CONFIG.ante));
  const startingStackSliderIndex = findClosestOptionIndex(STARTING_STACK_OPTIONS, tableStartingStack);
  const anteSliderIndex = findClosestOptionIndex(ANTE_OPTIONS, tableAnte);
  const hasEnoughBalanceForSetup = userBalance >= setupStartingStack;
  const hasValidSetupConfig = setupStartingStack >= setupAnte;
  const canStartFreshSession = hasEnoughBalanceForSetup && hasValidSetupConfig;
  /**
   * Олл-ин в торговле / на вскрытии: стек 0, но игрок ещё в раздаче (не сбросил, не marked sitting out).
   * Нельзя путать с «нужна докупка на следующую руку».
   */
  const heroAllInContestingPot = Boolean(
    gameStarted &&
      !game.gameEnded &&
      heroPlayer &&
      heroTableChips <= 0 &&
      !heroPlayer.isFolded &&
      !heroPlayer.isSittingOut
  );
  /** Вне текущей раздачи (наблюдатель / вне игры). Не считать олл-ина в руке «вне игры». */
  const heroSittingOut = Boolean(
    gameStarted &&
      !game.gameEnded &&
      heroPlayer &&
      (heroPlayer.isSittingOut || (heroTableChips <= 0 && !heroAllInContestingPot))
  );
  /** На столе 0 фишек и игрок уже не в текущей раздаче — нужна докупка (анте / свара). */
  const heroNeedsAnteHelp = Boolean(
    gameStarted && !game.gameEnded && heroPlayer && heroTableChips <= 0 && !heroAllInContestingPot
  );
  const minBuyIn = Math.max(ante, effAnteNext);
  const maxBuyIn = userBalance;
  const canBuyIn = maxBuyIn >= minBuyIn;
  const clampedBuyIn = Math.max(minBuyIn, Math.min(maxBuyIn, Math.floor(Number(buyInAmount) || 0)));
  const heroPendingBuyIn = Number(heroPlayer?.pendingBuyIn || 0);
  const shouldShowHeroOutModal =
    heroNeedsAnteHelp &&
    !game.gameEnded &&
    heroPendingBuyIn <= 0 &&
    !showBuyInModal &&
    !heroOutNoticeCollapsed &&
    !spectatorObservingMatch;

  useEffect(() => {
    if (heroTableChips > 0) {
      setSpectatorObservingMatch(false);
    }
  }, [heroTableChips]);

  useEffect(() => {
    if (!heroNeedsAnteHelp) {
      setShowBuyInModal(false);
      setHeroOutNoticeCollapsed(false);
      return;
    }
    setShowRaiseInput(false);
    if (canBuyIn) {
      setBuyInAmount((prev) => {
        const n = Math.floor(Number(prev) || 0);
        if (n < minBuyIn) return minBuyIn;
        if (n > maxBuyIn) return maxBuyIn;
        return n;
      });
      return;
    }
    setBuyInAmount(0);
  }, [canBuyIn, heroNeedsAnteHelp, maxBuyIn, minBuyIn]);

  useEffect(() => {
    if (heroPendingBuyIn > 0 || game.gameEnded || showBuyInModal) {
      setHeroOutNoticeCollapsed(false);
    }
  }, [game.gameEnded, heroPendingBuyIn, showBuyInModal]);

  const openBuyInModal = useCallback(() => {
    if (!heroNeedsAnteHelp || !canBuyIn) return;
    setSpectatorObservingMatch(false);
    setBuyInAmount((prev) => {
      const n = Math.floor(Number(prev) || 0);
      if (n < minBuyIn) return minBuyIn;
      if (n > maxBuyIn) return maxBuyIn;
      return n;
    });
    setShowBuyInModal(true);
  }, [canBuyIn, heroNeedsAnteHelp, maxBuyIn, minBuyIn]);

  const applyLocalBuyIn = useCallback(() => {
    if (!heroNeedsAnteHelp || !canBuyIn) return;
    const amount = clampedBuyIn;
    if (!Number.isFinite(amount) || amount < minBuyIn) return;
    if (amount > maxBuyIn) return;
    let mode = 'queued';
    setGame((prevGame) => {
      if (!prevGame) return prevGame;
      const next = cloneLocalGame(prevGame);
      const buyIn = next.requestBuyIn?.(0, amount);
      if (!buyIn?.ok) return prevGame;
      mode = String(buyIn.mode || 'queued');
      return next;
    });
    setNotifications((prev) => [
      ...prev,
      {
        id: `buyin-deduct-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        message:
          mode === 'instant'
            ? `Докупка применена сразу: -${amount.toLocaleString('ru-RU')} SEKA`
            : `Докупка в очереди: -${amount.toLocaleString('ru-RU')} SEKA`,
        kind: 'debit',
      },
    ]);
    onUserPatch?.({ ...localSekaWalletPatchMeta, balance: userBalance - amount });
    setShowBuyInModal(false);
  }, [canBuyIn, clampedBuyIn, heroNeedsAnteHelp, localSekaWalletPatchMeta, maxBuyIn, minBuyIn, onUserPatch, userBalance]);

  const blindsLabel = `Раунд ${game.roundNumber} · ставка ${game.currentBet} SEKA`;
  const heroCurrentChips = Number(game.players?.[0]?.chips ?? 0);
  const isAbsoluteVictory = Boolean(
    game.gameEnded &&
      game.winner?.id === 1 &&
      game.players.length > 1 &&
      game.players.slice(1).every((p) => Number(p?.chips ?? 0) <= ante)
  );
  const sessionStartHeroStack = Number(sessionStartHeroStackRef.current || 0);
  const absoluteVictoryNetProfit = Math.max(0, heroCurrentChips - sessionStartHeroStack);
  const outcomeKind = !game.gameEnded
    ? null
    : game.winner
      ? game.winner.id === 1
        ? 'win'
        : 'lose'
      : (game.winners || []).some((w) => w.id === 1)
        ? 'chop'
        : 'lose';
  const isNoEligibleTerminalState = Boolean(
    game.gameEnded &&
      !game.winner &&
      (!Array.isArray(game.winners) || game.winners.length === 0) &&
      Array.isArray(game.players) &&
      game.players.length > 0 &&
      game.players.every((p) => Boolean(p?.isFolded) || Boolean(p?.isSittingOut))
  );
  const spectatorMatchWinner =
    game.gameEnded &&
    outcomeKind === 'lose' &&
    heroPlayer?.isSittingOut &&
    heroCurrentChips <= 0
      ? game.winner || (Array.isArray(game.winners) && game.winners.length > 0 ? game.winners[0] : null)
      : null;
  const isSpectatorMatchOver = Boolean(
    isNoEligibleTerminalState ||
      spectatorMatchWinner ||
      (game.gameEnded && outcomeKind === 'lose' && heroCurrentChips <= 0)
  );
  const spectatorReentryMinBuyIn = Math.max(ante + 1, 1);
  const spectatorReentryShortage = Math.max(0, spectatorReentryMinBuyIn - heroCurrentChips);
  const canSpectatorReenter = spectatorReentryShortage > 0 && userBalance >= spectatorReentryShortage;
  const botsEligibleToContinue = (game.players || []).slice(1).filter((p) => Number(p?.chips ?? 0) > 0).length;
  /** Наблюдать имеет смысл только если минимум два бота со стеком (иначе некому играть следующую раздачу). */
  const canContinueWatchingBots = game.gameEnded && heroCurrentChips <= 0 && botsEligibleToContinue >= 2;
  /** Герой без стека, у двух+ ботов ещё есть фишки — матч между ними может продолжиться. */
  const spectatorHeroBustedOthersContinue = Boolean(
    game.gameEnded &&
      heroCurrentChips <= 0 &&
      botsEligibleToContinue >= 2 &&
      !isNoEligibleTerminalState
  );
  const winnerSummary = game.winner
    ? `Победитель: ${game.winner.name}`
    : game.winners && game.winners.length > 0
      ? `Ничья: ${game.winners.map((w) => w.name).join(' · ')}`
      : null;
  const heroChipsDelta =
    game.gameEnded && game.players[0] != null && heroStackAtHandStartRef.current != null
      ? game.players[0].chips - heroStackAtHandStartRef.current
      : null;
  const heavyLossThreshold = sessionStartHeroStack > 0 ? sessionStartHeroStack / 4 : Number.POSITIVE_INFINITY;
  const heroSvarkaSeat = svarkaCommitSeats.find((s) => s.seatIndex === 0) || null;
  const heroCanChooseSvarka = Boolean(
    svarkaCommitPhase === 'collecting' && heroSvarkaSeat?.canParticipate && !heroPlayer?.isSittingOut
  );
  const svarkaCountdownRatio = Math.max(0, Math.min(1, svarkaCommitSeconds / SVARKA_COMMIT_SECONDS));
  const svarkaCountdownWidth = `${Math.round(svarkaCountdownRatio * 1000) / 10}%`;
  const svarkaCountdownShouldAnimateWidth =
    svarkaCommitPhase === 'collecting' && svarkaCommitSeconds < SVARKA_COMMIT_SECONDS;
  const svarkaCountdownLineClass =
    svarkaCommitSeconds <= 1
      ? 'from-red-400 via-red-500 to-red-600 shadow-[0_0_16px_rgba(248,113,113,0.6)]'
      : svarkaCommitSeconds <= 2
        ? 'from-orange-300 via-orange-400 to-red-500 shadow-[0_0_14px_rgba(251,146,60,0.55)]'
        : svarkaCommitSeconds <= 3
          ? 'from-amber-300 via-amber-400 to-orange-500 shadow-[0_0_12px_rgba(251,191,36,0.5)]'
          : 'from-emerald-300 via-emerald-400 to-amber-300 shadow-[0_0_12px_rgba(52,211,153,0.45)]';
  const matchHistoryRows = useMemo(
    () =>
      (matchHistory || []).map((hist, idx) => {
        const handIndex = idx + 1;
        const winnerNames = Array.isArray(hist.winners) && hist.winners.length > 0
          ? hist.winners.map((w) => w.name).join(' · ')
          : hist.winner?.name || 'Ничья';
        const deltas = Array.isArray(hist.stackDeltas) ? hist.stackDeltas : [];
        const actions = Array.isArray(hist.actions) ? hist.actions : [];
        const heroDeltaObj =
          deltas.find((d) => Number(d.playerId) === 1) ||
          deltas.find((d) => String(d.playerName || '').toLowerCase() === String(heroName || '').toLowerCase()) ||
          null;
        const heroDelta = Number(heroDeltaObj?.delta || 0);
        const sortedDeltas = [...deltas].sort((a, b) => Number(b.delta || 0) - Number(a.delta || 0));
        const endType = describeSekaHandEndType(hist, actions);
        const hasHeroActivity = actions.some((a) => Number(a?.playerId) === 1);
        const playerSnapshots = buildPlayerSnapshotsFromHistoryEntry(hist);
        return {
          id: `h-${handIndex}-${hist.timestamp || idx}`,
          handIndex,
          winnerNames,
          pot: Number(hist.pot || 0),
          timestamp: hist.timestamp ? new Date(hist.timestamp) : null,
          deltas: sortedDeltas,
          actions,
          heroDelta,
          endType,
          hasHeroActivity,
          playerSnapshots,
        };
      }),
    [heroName, matchHistory]
  );
  const filteredMatchHistoryRows = useMemo(() => {
    if (historyFilter === 'hero') {
      return matchHistoryRows.filter((row) => row.hasHeroActivity || row.heroDelta !== 0);
    }
    if (historyFilter === 'big') {
      return matchHistoryRows.filter((row) => row.pot >= ante * 3);
    }
    return matchHistoryRows;
  }, [ante, historyFilter, matchHistoryRows]);

  const copyMatchHistoryDoneTimerRef = useRef(null);
  const [matchHistoryCopyFeedback, setMatchHistoryCopyFeedback] = useState(false);

  const handleCopyFullMatchHistory = useCallback(async () => {
    const text = buildFullMatchHistoryExportText(matchChronicle, matchHistoryRows);
    try {
      await navigator.clipboard.writeText(text);
      setMatchHistoryCopyFeedback(true);
      if (copyMatchHistoryDoneTimerRef.current) {
        clearTimeout(copyMatchHistoryDoneTimerRef.current);
      }
      copyMatchHistoryDoneTimerRef.current = setTimeout(() => {
        setMatchHistoryCopyFeedback(false);
        copyMatchHistoryDoneTimerRef.current = null;
      }, 2200);
    } catch {
      setNotifications((prev) => [
        ...prev,
        {
          id: `copy-fail-${Date.now()}`,
          message: 'Не удалось скопировать в буфер',
          kind: 'info',
        },
      ]);
    }
  }, [matchChronicle, matchHistoryRows]);

  const outcomeShowdownPanel = useMemo(() => {
    if (!game.gameEnded || !outcomeKind || isAbsoluteVictory) return null;
    if (outcomeKind !== 'lose' && outcomeKind !== 'win' && outcomeKind !== 'chop') return null;
    const explain =
      outcomeKind === 'lose'
        ? buildLossShowdownExplain(game)
        : outcomeKind === 'win'
          ? buildWinShowdownExplain(game)
          : buildChopShowdownExplain(game);
    return {
      rows: buildShowdownRows(game, handStartStacksRef.current),
      explain,
      variant: outcomeKind,
    };
  }, [game, outcomeKind, isAbsoluteVictory]);
  /** Журнал ходов завершённой раздачи — для панели победы / поражения / ничьей. */
  const outcomeModalHandActions = useMemo(() => {
    if (!game.gameEnded) return [];
    const gh = Array.isArray(game.gameHistory) ? game.gameHistory : [];
    const last = gh.length > 0 ? gh[gh.length - 1] : null;
    return last && Array.isArray(last.actions) ? last.actions : [];
  }, [game.gameEnded, game.gameHistory]);

  /** Последняя запись gameHistory — банк, дельты по стеку (вкл. анте), победитель; для окна исхода без рассинхрона с matchHistory. */
  const outcomeModalHistorySnapshot = useMemo(() => {
    if (!game.gameEnded) return null;
    const gh = Array.isArray(game.gameHistory) ? game.gameHistory : [];
    const hist = gh.length > 0 ? gh[gh.length - 1] : null;
    if (!hist) return null;
    const deltas = Array.isArray(hist.stackDeltas) ? hist.stackDeltas : [];
    const heroDeltaObj =
      deltas.find((d) => Number(d.playerId) === 1) ||
      deltas.find(
        (d) => String(d.playerName || '').toLowerCase() === String(heroName || '').toLowerCase()
      ) ||
      null;
    const hasHeroDelta = Boolean(heroDeltaObj) && Number.isFinite(Number(heroDeltaObj.delta));
    const heroDelta = hasHeroDelta ? Number(heroDeltaObj.delta) : null;
    const sortedDeltas = [...deltas].sort((a, b) => Number(b.delta || 0) - Number(a.delta || 0));
    const winnerNames =
      Array.isArray(hist.winners) && hist.winners.length > 0
        ? hist.winners.map((w) => w?.name).filter(Boolean).join(' · ')
        : hist.winner?.name || 'Ничья';
    return {
      pot: Number(hist.pot || 0),
      winnerNames,
      hasHeroDelta,
      heroDelta,
      sortedDeltas,
    };
  }, [game.gameEnded, game.gameHistory, heroName]);

  /** Дельта за раздачу из истории (предпочтительно); иначе снимок после анте из ref. */
  const heroOutcomeDisplayDelta = useMemo(() => {
    if (outcomeModalHistorySnapshot?.hasHeroDelta) return outcomeModalHistorySnapshot.heroDelta;
    return heroChipsDelta;
  }, [outcomeModalHistorySnapshot, heroChipsDelta]);

  const isHeavyLossSound = useMemo(
    () =>
      outcomeKind === 'lose' &&
      heroOutcomeDisplayDelta != null &&
      heroOutcomeDisplayDelta < 0 &&
      Math.abs(heroOutcomeDisplayDelta) > heavyLossThreshold,
    [outcomeKind, heroOutcomeDisplayDelta, heavyLossThreshold]
  );

  /** Ходы текущей раздачи в реальном времени (только действия, без карт соперников). */
  const liveHandActionLog = useMemo(() => {
    if (game.gameEnded) return [];
    const log = game.currentHandActionLog;
    return Array.isArray(log) ? [...log] : [];
  }, [game]);

  /** Последняя строка сводки (та же, что в полной истории) — карты/ходы/дельты для окна исхода. */
  const outcomePanelLastRow = useMemo(() => {
    if (matchHistoryRows.length === 0) return null;
    return matchHistoryRows[matchHistoryRows.length - 1];
  }, [matchHistoryRows]);

  const outcomePanelSnapshots = useMemo(
    () => (outcomePanelLastRow?.playerSnapshots?.length ? outcomePanelLastRow.playerSnapshots : []),
    [outcomePanelLastRow]
  );

  const [outcomeCopyFeedback, setOutcomeCopyFeedback] = useState(false);
  const outcomeCopyTimerRef = useRef(null);

  const localOutcomeStingerKey = useMemo(() => {
    if (!game.gameEnded) return '';
    if (!outcomeKind) return '';
    if (outcomeKind !== 'win' && outcomeKind !== 'lose' && outcomeKind !== 'chop') return '';
    return (
      outcomePanelLastRow?.id ||
      `fb-${Array.isArray(game.gameHistory) ? game.gameHistory.length : 0}-${Number(game.roundNumber) || 0}`
    );
  }, [game.gameEnded, game.gameHistory, game.roundNumber, outcomeKind, outcomePanelLastRow?.id]);

  const localStingerPayload = useMemo(() => {
    if (!localOutcomeStingerKey) return null;
    const base = buildLocalSekaBotSessionOutcome({
      user,
      outcomeKind,
      botCount,
      userBalance,
      heroTableChips: heroCurrentChips,
      sekaDelta: heroOutcomeDisplayDelta,
      handKey: localOutcomeStingerKey,
    });
    if (!isAbsoluteVictory) return base;
    const profit = Math.max(0, Math.floor(Number(absoluteVictoryNetProfit) || 0));
    if (absoluteVictoryWalletForStingerRef.current == null) {
      absoluteVictoryWalletForStingerRef.current = userBalance;
    }
    const walletBeforeAbs = absoluteVictoryWalletForStingerRef.current;
    return {
      ...base,
      currencyDelta: { ...base.currencyDelta, seka: profit },
      balanceAfter: { ...base.balanceAfter, seka: walletBeforeAbs + profit },
      display: { ...base.display, sekaLineLabel: 'Кошелёк' },
    };
  }, [
    localOutcomeStingerKey,
    user,
    outcomeKind,
    botCount,
    userBalance,
    heroCurrentChips,
    heroOutcomeDisplayDelta,
    isAbsoluteVictory,
    absoluteVictoryNetProfit,
  ]);

  useEffect(() => {
    setLocalStingerClosed(false);
  }, [localOutcomeStingerKey]);

  const handleCopyOutcomePanel = useCallback(async () => {
    const text = buildOutcomePanelExportText(matchChronicle, outcomePanelLastRow);
    try {
      await navigator.clipboard.writeText(text);
      setOutcomeCopyFeedback(true);
      if (outcomeCopyTimerRef.current) clearTimeout(outcomeCopyTimerRef.current);
      outcomeCopyTimerRef.current = setTimeout(() => {
        setOutcomeCopyFeedback(false);
        outcomeCopyTimerRef.current = null;
      }, 2200);
    } catch {
      setNotifications((prev) => [
        ...prev,
        {
          id: `outcome-copy-fail-${Date.now()}`,
          message: 'Не удалось скопировать снимок',
          kind: 'info',
        },
      ]);
    }
  }, [matchChronicle, outcomePanelLastRow]);

  const statusMessage = game.gameEnded
    ? isAbsoluteVictory
      ? 'Абсолютная победа: у соперников нет фишек на анте следующей раздачи.'
      : isSpectatorMatchOver
        ? isNoEligibleTerminalState
          ? 'Матч завершен: ни у кого не хватило фишек для следующего анте.'
          : spectatorHeroBustedOthersContinue
            ? `У вас нет фишек на столе — вы вне раздач до докупки. Победитель раздачи: ${spectatorMatchWinner?.name || game.winner?.name || 'соперник'}. Соперники могут продолжить матч.`
            : `Матч завершен: победил ${spectatorMatchWinner?.name || 'соперник'}.`
      : 'Раздача завершена.'
    : heroSittingOut
      ? heroPendingBuyIn > 0
        ? `Вы вне раздачи. Докупка ${heroPendingBuyIn.toLocaleString('ru-RU')} SEKA применится со следующей руки.`
        : heroNeedsAnteHelp
          ? 'Вы вне раздачи (недостаточно фишек для анте). Можно докупиться и ждать следующую руку.'
          : 'Вы пропускаете эту раздачу. Следующая рука — снова в игре.'
    : game.currentPlayer === 0
      ? `Ваш ход (раунд ${game.roundNumber}).`
      : `Ходит ${game.players[game.currentPlayer]?.name || 'бот'}. Ожидайте…`;

  const handleSpectatorReenterClick = useCallback(() => {
    if (!isSpectatorMatchOver || !canSpectatorReenter) return;
    const amount = spectatorReentryShortage;
    if (!Number.isFinite(amount) || amount <= 0 || userBalance < amount) return;

    clearDealTimers();
    stopTurnTimer();

    const seeds = buildPlayerSeeds(heroName.trim() || 'Игрок', botCount);
    const selectedAnte = Math.max(1, Math.floor(Number(tableAnte) || DEFAULT_SEKA_CONFIG.ante));
    const startStack = Math.max(1, Math.floor(Number(tableStartingStack) || DEFAULT_SEKA_CONFIG.startingChips));
    if (!(startStack > selectedAnte)) return;

    const gameConfig = {
      startingChips: startStack,
      ante: selectedAnte,
      minRaise: Math.max(1, selectedAnte),
    };
    const nextGame = new LocalSekaGame(gameConfig, seeds);
    nextGame.gameHistory = [...matchHistory];
    const sameShape = game.players.length === seeds.length;
    if (sameShape) {
      nextGame.dealerIndex = (game.dealerIndex + 1) % Math.max(nextGame.players.length, 1);
      nextGame.players = nextGame.players.map((p, i) => ({
        ...p,
        chips: Math.max(0, Number(game.players[i]?.chips ?? p.chips)) + (i === 0 ? amount : 0),
      }));
    } else {
      nextGame.players = nextGame.players.map((p, i) => ({
        ...p,
        chips: Math.max(0, Number(p.chips || 0)) + (i === 0 ? amount : 0),
      }));
    }

    setNotifications((prev) => [
      ...prev,
      {
        id: `buyin-reentry-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        message: `Докупка: -${amount.toLocaleString('ru-RU')} SEKA`,
        kind: 'debit',
      },
    ]);
    onUserPatch?.({ ...localSekaWalletPatchMeta, balance: userBalance - amount });

    dealGenRef.current += 1;
    const gen = dealGenRef.current;
    const prep = nextGame.prepareNewHand();
    if (!prep.canDealHole) {
      heroStackAtHandStartRef.current = nextGame.players[0]?.chips ?? 0;
      handStartStacksRef.current = nextGame.players.map((p) => (p ? p.chips : null));
      setDealRevealed(null);
      setGame(cloneLocalGame(nextGame));
      setGameStarted(true);
      setShowCards(Array.from({ length: seeds.length }, () => false));
      setLastSeatActions(Array.from({ length: seeds.length }, () => null));
      setBotTurnClock(null);
      setShowRaiseInput(false);
      setLocalAntePhaseText('');
      setLocalAnteFlights([]);
      return;
    }

    nextGame.collectAnteFromEligible(prep.eligiblePlayers);
    heroStackAtHandStartRef.current = nextGame.players[0]?.chips ?? 0;
    handStartStacksRef.current = nextGame.players.map((p) => (p ? p.chips : null));

    const mult = Math.max(1, Number(nextGame.svarkaAnteMultiplier) || 1);
    const eff = nextGame.getEffectiveAnteAmount();
    const anteBannerText = buildLocalAntePhaseBannerText(mult, eff, prep.eligiblePlayers);

    scheduleAnteAndDeal(nextGame, prep, gen, seeds.length, anteBannerText);
  }, [
    botCount,
    canSpectatorReenter,
    clearDealTimers,
    game,
    heroName,
    isSpectatorMatchOver,
    localSekaWalletPatchMeta,
    matchHistory,
    onUserPatch,
    scheduleAnteAndDeal,
    spectatorReentryShortage,
    stopTurnTimer,
    tableAnte,
    tableStartingStack,
    userBalance,
  ]);

  useEffect(() => {
    const incoming = Array.isArray(game.gameHistory) ? game.gameHistory : [];
    if (incoming.length === 0) return;
    setMatchHistory((prev) => mergeHistoryUnique(prev, incoming));
  }, [game.gameHistory]);

  useEffect(() => {
    const list = Array.isArray(matchHistory) ? matchHistory : [];
    list.forEach((h, idx) => {
      const key = buildHistoryKey(h, idx);
      if (chronicleHandKeysRef.current.has(key)) return;
      chronicleHandKeysRef.current.add(key);
      const handNum = idx + 1;
      const pot = Number(h.pot || 0);
      const actions = Array.isArray(h.actions) ? h.actions : [];
      const chain = actions.map(
        (a, i) => `${i + 1}. ${a.playerName}: ${formatHistoryAction(a)}`
      );
      if (chain.length === 0) {
        chain.push('Торговля не записана или авто-завершение');
      }
      setMatchChronicle((prev) => [
        ...prev,
        {
          id: `hand-${key}`,
          ts: h.timestamp ? new Date(h.timestamp).getTime() : Date.now(),
          kind: 'hand',
          title: `Раздача #${handNum} · банк ${pot.toLocaleString('ru-RU')} SEKA`,
          subtitle: `Победитель: ${winnerNamesFromHistoryEntry(h)}`,
          steps: chain,
        },
      ]);
    });
  }, [matchHistory]);

  useEffect(() => {
    if (!isAbsoluteVictory) return;
    if (absolutePayoutAppliedRef.current) return;
    absolutePayoutAppliedRef.current = true;
    if (absoluteVictoryNetProfit <= 0) return;
    tableProfitCreditedToWalletRef.current = absoluteVictoryNetProfit;
    const currentBalance = Math.max(0, Math.floor(Number(user?.balance ?? 0)));
    setNotifications((prev) => [
      ...prev,
      {
        id: `absolute-credit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        message: `Абсолютная победа: +${absoluteVictoryNetProfit.toLocaleString('ru-RU')} SEKA`,
        kind: 'credit',
      },
    ]);
    onUserPatch?.({ ...localSekaWalletPatchMeta, balance: currentBalance + absoluteVictoryNetProfit });
  }, [absoluteVictoryNetProfit, isAbsoluteVictory, localSekaWalletPatchMeta, onUserPatch, user?.balance]);

  useEffect(() => {
    if (!game.gameEnded) {
      if (outcomeSoundDelayRef.current) {
        clearTimeout(outcomeSoundDelayRef.current);
        outcomeSoundDelayRef.current = null;
      }
      losePanelSoundPlayedRef.current = false;
      winPanelSoundPlayedRef.current = false;
      winnerFanfareAfterStingerCloseRef.current = false;
      return;
    }
    if (isAbsoluteVictory) return;
    if (outcomeKind === 'lose') {
      if (losePanelSoundPlayedRef.current) return;
      losePanelSoundPlayedRef.current = true;
      initAudio();
      outcomeSoundDelayRef.current = setTimeout(() => {
        play(isHeavyLossSound ? 'lose-heavy' : 'lose-game', 1);
        outcomeSoundDelayRef.current = null;
      }, 60);
      return;
    }
    if (outcomeKind === 'win') {
      if (winPanelSoundPlayedRef.current) return;
      winPanelSoundPlayedRef.current = true;
      initAudio();
      const hasMoneyStinger = Boolean(localStingerPayload) && !localStingerClosed;
      if (hasMoneyStinger) {
        winnerFanfareAfterStingerCloseRef.current = true;
        return;
      }
      outcomeSoundDelayRef.current = setTimeout(() => {
        play('winner-game', 1);
        outcomeSoundDelayRef.current = null;
      }, 60);
    }
  }, [
    game.gameEnded,
    initAudio,
    isAbsoluteVictory,
    isHeavyLossSound,
    localStingerClosed,
    localStingerPayload,
    outcomeKind,
    play,
  ]);

  useEffect(() => {
    if (!gameStarted) return;
    setSeatOffsetBySeat(loadOffsetsForBlock(SEAT_OFFSET_STORAGE.local, String(botCount + 1)));
  }, [gameStarted, botCount]);

  const handleSeatDragPointerDown = useCallback(
    (e, seat, slotLeft, slotTop) => {
      if (!seatArrangeMode) return;
      const arena = seatArenaRef.current;
      if (!arena) return;
      e.preventDefault();
      e.stopPropagation();
      const rect = arena.getBoundingClientRect();
      const w = Math.max(1, rect.width);
      const h = Math.max(1, rect.height);
      const o = seatOffsetBySeat[seat] || { dLeft: 0, dTop: 0 };
      seatDragRef.current = {
        pointerId: e.pointerId,
        seat,
        slotLeft,
        slotTop,
        odl: o.dLeft,
        odt: o.dTop,
        sx: e.clientX,
        sy: e.clientY,
        aw: w,
        ah: h,
      };
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    },
    [seatArrangeMode, seatOffsetBySeat]
  );

  const handleSeatDragPointerMove = useCallback((e) => {
    const d = seatDragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    const dxPct = ((e.clientX - d.sx) / d.aw) * 100;
    const dyPct = ((e.clientY - d.sy) / d.ah) * 100;
    let nl = d.odl + dxPct;
    let nt = d.odt + dyPct;
    const c = clampSeatCenter(d.slotLeft + nl, d.slotTop + nt);
    nl = c.leftPct - d.slotLeft;
    nt = c.topPct - d.slotTop;
    setSeatOffsetBySeat((prev) => {
      const cur = prev[d.seat];
      if (cur && cur.dLeft === nl && cur.dTop === nt) return prev;
      return { ...prev, [d.seat]: { dLeft: nl, dTop: nt } };
    });
  }, []);

  const handleSeatDragPointerUp = useCallback(
    (e) => {
      const d = seatDragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;
      seatDragRef.current = null;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      setSeatOffsetBySeat((prev) => {
        saveOffsetsForBlock(SEAT_OFFSET_STORAGE.local, String(botCount + 1), prev);
        return prev;
      });
    },
    [botCount]
  );

  const handleSeatDragLostCapture = useCallback(() => {
    const had = seatDragRef.current != null;
    seatDragRef.current = null;
    if (had) {
      setSeatOffsetBySeat((prev) => {
        saveOffsetsForBlock(SEAT_OFFSET_STORAGE.local, String(botCount + 1), prev);
        return prev;
      });
    }
  }, [botCount]);

  const handleClearSeatOffsets = useCallback(() => {
    clearOffsetsForBlock(SEAT_OFFSET_STORAGE.local, String(botCount + 1));
    setSeatOffsetBySeat({});
  }, [botCount]);

  const activeSeats = game.players
    .map((p, seat) => (p ? seat : null))
    .filter((seat) => seat != null);
  const seatDensity = getSeatDensityPreset(activeSeats.length);
  const seatArena = getSeatArenaTuning(activeSeats.length);
  const seatSlots = buildEllipticSeatSlots({
    seats: activeSeats,
    heroSeat: 0,
    centerY: seatArena.centerY,
    radiusX: seatArena.radiusX,
    radiusY: seatArena.radiusY,
  });

  if (!gameStarted) {
    return (
      <div className="pt-root relative flex h-[100dvh] max-h-[100dvh] min-h-0 flex-col overflow-hidden bg-background text-foreground">
        <ArcadeModeBackdrop mode="seka" />
        <GameIndustrialHeader
          floating
          tableName="Сека · игра с ботами"
          blindsLabel="Настройка стола"
          playersCount={botCount + 1}
          maxPlayers={4}
          bucksBalance={user?.bucksBalance}
          balance={userBalance}
          onLeave={onBack}
        />
        <div className="relative z-10 flex min-h-0 flex-1 flex-col overflow-hidden">
          <main className="relative z-10 mx-auto flex min-h-0 w-full max-w-xl flex-1 flex-col gap-3 overflow-y-auto overscroll-y-contain px-3 pb-3 pt-3 sm:gap-4 sm:px-4">
            <div className="grid min-h-0 grid-cols-1 gap-3 sm:landscape:grid-cols-2 sm:landscape:items-stretch sm:landscape:gap-4">
              {/*
                Левая колонка без min-h-0: иначе flex-item с логотипом может сжаться до ~padding (20px)
                при sm:landscape + align-items:start у сетки.
              */}
              <div className="flex flex-col gap-3">
                <div className="relative shrink-0 overflow-hidden rounded-2xl border border-amber-300/25 bg-gradient-to-br from-[#4a280f]/88 via-[#3a1f0c]/84 to-[#2b1608]/88 px-3 py-2.5 shadow-[0_18px_46px_rgba(0,0,0,0.4)] sm:landscape:py-2 min-h-[5.5rem] sm:min-h-[6rem]">
                  <div className="pointer-events-none absolute inset-0" aria-hidden>
                    <div className="absolute left-[10%] top-[12%] h-20 w-20 rounded-full bg-amber-300/20 blur-2xl" />
                    <div className="absolute right-[12%] bottom-[10%] h-16 w-16 rounded-full bg-orange-300/16 blur-2xl" />
                  </div>
                  <img
                    src="/logotip-seka-button.png"
                    alt=""
                    className="relative z-[1] mx-auto block h-auto min-h-[3rem] w-full max-h-[78px] max-w-[min(320px,92%)] origin-center object-contain object-center transition-transform duration-200 ease-out sm:max-h-[88px] sm:landscape:max-h-[64px]"
                    aria-hidden="true"
                    draggable={false}
                  />
                </div>

                <section className="shrink-0 overflow-visible bg-transparent shadow-none">
                  <div className="flex flex-col items-center px-0 py-1 text-center sm:landscape:items-start sm:landscape:text-left">
                    <div className="mb-2 flex shrink-0 items-center justify-center bg-transparent shadow-none sm:landscape:mb-1.5 sm:landscape:justify-start">
                      <Swords className="h-24 w-24 text-amber-200 landscape:h-14 landscape:w-14 sm:landscape:h-20 sm:landscape:w-20" />
                    </div>
                    <p className="text-base font-black text-amber-100 sm:text-[1.05rem]">
                      Локальный матч Секи против ботов
                    </p>
                    <p className="mt-1 max-w-[34rem] text-xs leading-relaxed text-slate-300/90 sm:text-[13px]">
                      Быстрый режим с индустриальным HUD: настройте стол и стартуйте раздачу в один тап.
                    </p>
                  </div>
                </section>
              </div>

              <div className="flex min-h-0 flex-col gap-3">
          <section className="rounded-2xl border border-white/10 bg-gradient-to-b from-[#111a2a]/92 to-[#0b1320]/92 p-3.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] sm:p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <p className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">Параметры стола</p>
              <span className="rounded-full border border-amber-300/30 bg-amber-400/10 px-2 py-0.5 text-[10px] font-semibold text-amber-100">
                До 4 игроков
              </span>
            </div>

            <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Количество ботов
            </label>
            <div className="grid grid-cols-3 gap-2">
              {BOT_COUNT_OPTIONS.map((count) => (
                <button
                  key={count}
                  type="button"
                  onClick={() => setBotCount(count)}
                  aria-label={`${count} бота(ов)`}
                  className={cn(
                    'group overflow-hidden rounded-xl border px-2 py-2.5 text-sm font-bold transition min-h-[62px]',
                    botCount === count
                      ? 'border-amber-300/65 bg-amber-400/15 text-amber-100 shadow-[0_0_0_1px_rgba(252,211,77,0.22)]'
                      : 'border-white/10 bg-[#0a1626]/70 text-slate-200 hover:border-amber-300/35 hover:bg-amber-500/10'
                  )}
                >
                  <span className="inline-flex items-center justify-center gap-1 sm:gap-1.5">
                    {Array.from({ length: count }).map((_, idx) => (
                      <Bot
                        key={`${count}-bot-icon-${idx}`}
                        strokeWidth={2.35}
                        className={cn(
                          count === 1 ? 'h-9 w-9 sm:h-10 sm:w-10' : count === 2 ? 'h-7 w-7 sm:h-8 sm:w-8' : 'h-6 w-6 sm:h-7 sm:w-7',
                          'drop-shadow-[0_0_10px_rgba(251,191,36,0.28)]',
                          botCount === count ? 'text-amber-200' : 'text-slate-400 group-hover:text-amber-200'
                        )}
                      />
                    ))}
                  </span>
                </button>
              ))}
            </div>

            <div className="mt-3 rounded-xl border border-emerald-300/20 bg-emerald-500/[0.06] px-2.5 py-2.5">
              <div className="mb-2 flex items-center justify-between gap-2">
                <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-300">
                  Стартовый стек
                </label>
                <span className="font-mono text-sm font-black text-emerald-200">
                  {setupStartingStack.toLocaleString('ru-RU')}
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={STARTING_STACK_OPTIONS.length - 1}
                step={1}
                value={startingStackSliderIndex}
                onChange={(e) => {
                  const index = Number(e.target.value);
                  setTableStartingStack(STARTING_STACK_OPTIONS[index]);
                }}
                className="w-full accent-emerald-400"
                aria-label="Стартовый стек"
              />
              <div className="mt-1.5 flex items-center justify-between text-[10px] text-slate-500">
                <span>Мин {STARTING_STACK_OPTIONS[0].toLocaleString('ru-RU')}</span>
                <span>Макс {STARTING_STACK_OPTIONS[STARTING_STACK_OPTIONS.length - 1].toLocaleString('ru-RU')}</span>
              </div>
            </div>

            <div className="mt-3 rounded-xl border border-amber-300/20 bg-amber-500/[0.06] px-2.5 py-2.5">
              <div className="mb-2 flex items-center justify-between gap-2">
                <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-300">
                  Минимальный взнос (анте)
                </label>
                <span className="font-mono text-sm font-black text-amber-200">
                  {setupAnte.toLocaleString('ru-RU')}
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={ANTE_OPTIONS.length - 1}
                step={1}
                value={anteSliderIndex}
                onChange={(e) => {
                  const index = Number(e.target.value);
                  setTableAnte(ANTE_OPTIONS[index]);
                }}
                className="w-full accent-amber-300"
                aria-label="Минимальный взнос (анте)"
              />
              <div className="mt-1.5 flex items-center justify-between text-[10px] text-slate-500">
                <span>Мин {ANTE_OPTIONS[0].toLocaleString('ru-RU')}</span>
                <span>Макс {ANTE_OPTIONS[ANTE_OPTIONS.length - 1].toLocaleString('ru-RU')}</span>
              </div>
            </div>

            <p className="mt-2.5 text-[11px] text-slate-400">
              Матчап: <span className="font-semibold text-amber-100">{heroName || 'Игрок'}</span> vs {botCount} бота(ов)
            </p>
            <p className="mt-1 text-[11px] text-slate-400">
              Стек: <span className="font-semibold text-emerald-200">{setupStartingStack.toLocaleString('ru-RU')}</span> ·
              Анте: <span className="font-semibold text-amber-200">{setupAnte.toLocaleString('ru-RU')}</span> ·
              Баланс: <span className="font-semibold text-white">{userBalance.toLocaleString('ru-RU')}</span>
            </p>
            {!hasValidSetupConfig && (
              <p className="mt-1 text-[11px] text-red-300">Стартовый стек должен быть больше анте.</p>
            )}
            {!hasEnoughBalanceForSetup && (
              <p className="mt-1 text-[11px] text-red-300">Недостаточно средств для выбранного стартового стека.</p>
            )}
          </section>

          <section className="rounded-2xl border border-amber-300/20 bg-gradient-to-b from-amber-500/[0.05] to-transparent p-3.5 text-xs leading-relaxed text-slate-300 sm:p-4 sm:landscape:max-h-[min(220px,36vh)] sm:landscape:overflow-y-auto sm:landscape:overscroll-y-contain sm:landscape:pr-1">
            <p className="mb-2 text-[11px] font-black uppercase tracking-[0.14em] text-amber-200">Коротко о комбинациях</p>
            <ul className="space-y-1.5">
              <li><span className="font-semibold text-amber-100">Сека</span> — три одинаковых номинала</li>
              <li><span className="font-semibold text-amber-100">Тройка</span> — три одной масти</li>
              <li><span className="font-semibold text-amber-100">Хлюст</span> — две одной масти</li>
              <li><span className="font-semibold text-amber-100">Темная</span> — ставки дешевле вдвое</li>
            </ul>
          </section>
              </div>
            </div>
          </main>

          <div className="relative z-20 shrink-0 border-t border-white/10 bg-background/95 px-3 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] backdrop-blur-md supports-[backdrop-filter]:bg-background/80 sm:px-4">
            <button
              type="button"
              onClick={() => {
                play('mode-choosen-button', 1);
                startFreshSession();
              }}
              disabled={!canStartFreshSession}
              className="mx-auto flex min-h-[48px] w-full max-w-md items-center justify-center rounded-2xl border border-emerald-300/45 bg-gradient-to-b from-emerald-500/25 to-emerald-700/20 py-3 text-sm font-black text-emerald-100 shadow-[0_12px_28px_rgba(16,185,129,0.2)] transition hover:border-emerald-200/65 hover:from-emerald-400/30 hover:to-emerald-700/28 disabled:opacity-50"
            >
              {canStartFreshSession
                ? `НАЧАТЬ · -${setupStartingStack.toLocaleString('ru-RU')} SEKA`
                : 'НАЧАТЬ'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-root relative flex min-h-screen flex-col overflow-hidden bg-background text-foreground">
      <ArcadeModeBackdrop mode="seka" />

      <GameIndustrialHeader
        floating
        tableName={`Сека · ${heroName} vs ${botCount} бот(ов)`}
        blindsLabel={blindsLabel}
        playersCount={game.players.length}
        maxPlayers={4}
        bucksBalance={user?.bucksBalance}
        balance={userBalance}
        onLeave={handleHeaderLeave}
        seatArrangeMode={seatArrangeMode}
        onSeatArrangeToggle={() => setSeatArrangeMode((v) => !v)}
        onSeatLayoutReset={handleClearSeatOffsets}
      />

      <LocalSekaLeaveModal
        open={leaveModalOpen}
        onClose={() => setLeaveModalOpen(false)}
        onConfirm={handleConfirmLeaveSession}
        heroChips={heroCurrentChips}
        sessionStartStack={sessionStartHeroStack}
        midHand={!game.gameEnded && game.gamePhase !== 'svarkaBetween'}
      />

      {gameStarted ? (
        <p className="px-3 py-0.5 text-center text-[10px] leading-snug text-muted-foreground">
          Ход героя: до {SEKA_HERO_TURN_SECONDS_CEIL} с (
          {SEKA_HERO_TURN_BUDGET_MS.toLocaleString('ru-RU')} мс). По таймауту — чек, если не нужно уравнивать,
          иначе пас (как на сервере).
        </p>
      ) : null}

      {(localAnteFlights.length > 0 || localAntePhaseText) && (
        <div className="pointer-events-none fixed inset-0 z-[120]" aria-live="polite">
          {localAnteFlights.map((flight) => (
            <React.Fragment key={flight.id}>
              <span
                className="ante-fly-number"
                style={{
                  left: `${flight.startX}px`,
                  top: `${flight.startY}px`,
                  '--ante-dx': `${flight.dx}px`,
                  '--ante-dy': `${flight.dy}px`,
                  animationDelay: `${flight.delayMs}ms`,
                }}
              >
                -{Number(flight.amount || 0).toLocaleString('ru-RU')} SEKA
              </span>
              <span
                className="ante-fly-coin"
                style={{
                  left: `${flight.startX}px`,
                  top: `${flight.startY}px`,
                  '--ante-dx': `${flight.coinDx}px`,
                  '--ante-dy': `${flight.coinDy}px`,
                  animationDelay: `${Math.max(0, Number(flight.delayMs || 0) + 55)}ms`,
                }}
                aria-hidden
              >
                ◉
              </span>
            </React.Fragment>
          ))}
        </div>
      )}

      <main className="relative z-10 flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-3 py-3">
        <div className="flex flex-wrap items-center justify-center gap-2 text-center text-[10px] text-muted-foreground sm:text-xs">
          <span className="rounded-full border border-border/60 bg-card/60 px-2 py-0.5 font-mono tabular-nums">
            Банк {game.pot.toLocaleString('ru-RU')} SEKA
          </span>
          <span className="rounded-full border border-border/60 bg-card/60 px-2 py-0.5 tabular-nums">
            {heroName}: {gameStats.heroWins}
          </span>
          <span className="rounded-full border border-border/60 bg-card/60 px-2 py-0.5 tabular-nums">
            Боты: {gameStats.botWins}
          </span>
          <span className="rounded-full border border-border/60 bg-card/60 px-2 py-0.5 tabular-nums">
            Игр {gameStats.totalGames}
          </span>
        </div>

        <div
          className={cn(
            'rounded-xl border px-3 py-2 text-center text-xs font-semibold shadow-inner',
            game.gameEnded
              ? 'border-neon-gold/30 bg-card/70 text-neon-gold'
              : game.currentPlayer === 0
                ? 'border-neon-cyan/35 bg-card/70 text-neon-cyan'
                : 'border-border/60 bg-card/60 text-muted-foreground'
          )}
          role="status"
          aria-live="polite"
        >
          {statusMessage}
        </div>

        {shouldShowHeroOutModal && (
          <div className="fixed inset-0 z-[64] overflow-y-auto overscroll-contain bg-black/40">
            <div className="flex min-h-[100dvh] w-full items-center justify-center p-3 py-3 sm:p-4">
              <div className="pointer-events-auto w-full max-w-[min(92vw,360px)] min-h-0 max-h-[min(85dvh,calc(100dvh-1.5rem))] overflow-y-auto rounded-2xl border border-amber-300/45 bg-gradient-to-b from-[#22170a]/95 to-[#120c07]/95 px-5 py-5 text-center shadow-[0_0_55px_rgba(251,191,36,0.2)]">
              <p className="text-lg font-black uppercase tracking-wide text-amber-100">Недостаточно фишек</p>
              <p className="mt-2 text-xs leading-relaxed text-amber-50/90">
                У вас меньше {minBuyIn.toLocaleString('ru-RU')} SEKA для анте следующей раздачи.
              </p>
              <p className="mt-1 text-[11px] text-amber-200/85">
                Баланс кошелька: {userBalance.toLocaleString('ru-RU')} SEKA
              </p>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={openBuyInModal}
                  disabled={!canBuyIn}
                  className={cn(
                    'inline-flex h-11 items-center justify-center rounded-lg border text-xs font-bold transition',
                    canBuyIn
                      ? 'border-emerald-300/55 bg-emerald-500/15 text-emerald-100 hover:bg-emerald-500/25'
                      : 'cursor-not-allowed border-border/70 bg-card/70 text-muted-foreground'
                  )}
                >
                  Докупиться
                </button>
                <button
                  type="button"
                  onClick={onBack}
                  className="inline-flex h-11 items-center justify-center rounded-lg border border-border/70 bg-card/70 text-xs font-bold text-foreground transition hover:bg-muted/40"
                >
                  Выйти
                </button>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSpectatorObservingMatch(true);
                  setHeroOutNoticeCollapsed(true);
                }}
                className="mt-2 inline-flex h-10 w-full items-center justify-center rounded-lg border border-amber-300/35 bg-amber-500/10 text-xs font-semibold text-amber-100 transition hover:bg-amber-500/20"
              >
                Следить за матчем
              </button>
              </div>
            </div>
          </div>
        )}

        {game.gameEnded &&
          isAbsoluteVictory &&
          (!localStingerPayload || localStingerClosed) && (
          <div
            className="fixed inset-0 z-[60] overflow-y-auto overscroll-contain bg-black/25"
            aria-live="polite"
          >
            <div className="flex min-h-[100dvh] w-full items-center justify-center p-3 py-3 sm:p-4">
              <div className="pointer-events-auto max-w-[min(92vw,360px)] min-h-0 max-h-[min(85dvh,calc(100dvh-1.5rem))] overflow-y-auto rounded-2xl border border-emerald-300/55 bg-gradient-to-b from-emerald-950/95 to-[#0a1520]/95 px-7 py-7 text-center shadow-[0_0_70px_rgba(52,211,153,0.28)]">
              <Trophy className="mx-auto mb-2 h-11 w-11 text-emerald-300" aria-hidden />
              <p className="text-xl font-black uppercase tracking-wide text-emerald-100">Абсолютная победа</p>
              <p className="mt-2 text-xs leading-relaxed text-emerald-50/90">
                У соперников недостаточно фишек для анте следующей раздачи.
              </p>
              <div className="mt-3 rounded-lg border border-emerald-300/35 bg-emerald-500/10 px-3 py-2">
                <p className="text-[11px] text-emerald-100/80">Начислено в кошелёк (чистая прибыль)</p>
                <p className="mt-0.5 font-mono text-lg font-black text-emerald-300">
                  +{absoluteVictoryNetProfit.toLocaleString('ru-RU')} SEKA
                </p>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={startFreshSession}
                  disabled={!canStartFreshSession}
                  className={cn(
                    'inline-flex items-center justify-center rounded-lg border px-3 py-2 text-xs font-bold transition',
                    canStartFreshSession
                      ? 'border-neon-cyan/45 bg-neon-cyan/15 text-neon-cyan hover:bg-neon-cyan/25'
                      : 'cursor-not-allowed border-border/70 bg-card/70 text-muted-foreground'
                  )}
                >
                  Играть заново
                </button>
                <button
                  type="button"
                  onClick={onBack}
                  className="inline-flex items-center justify-center rounded-lg border border-border/70 bg-card/70 px-3 py-2 text-xs font-bold text-foreground transition hover:bg-muted/40"
                >
                  Выйти
                </button>
                <button
                  type="button"
                  onClick={() => setShowMatchHistoryModal(true)}
                  className="col-span-2 inline-flex items-center justify-center gap-1.5 rounded-lg border border-amber-300/55 bg-amber-500/12 px-3 py-2 text-xs font-bold text-amber-100 transition hover:bg-amber-500/22"
                >
                  <History className="h-3.5 w-3.5" aria-hidden />
                  Смотреть историю ходов
                </button>
              </div>
              </div>
            </div>
          </div>
        )}

        {game.gameEnded &&
          isSpectatorMatchOver &&
          !isAbsoluteVictory &&
          (!localStingerPayload || localStingerClosed) && (
          <div
            className="fixed inset-0 z-[60] overflow-y-auto overscroll-contain bg-black/25"
            aria-live="polite"
          >
            <div className="flex min-h-[100dvh] w-full items-center justify-center p-3 py-3 sm:p-4">
              <div className="pointer-events-auto max-w-[min(92vw,360px)] min-h-0 max-h-[min(85dvh,calc(100dvh-1.5rem))] overflow-y-auto rounded-2xl border border-amber-300/50 bg-gradient-to-b from-amber-950/90 to-[#0a1520]/95 px-7 py-7 text-center shadow-[0_0_46px_rgba(251,191,36,0.2)]">
              <Trophy className="mx-auto mb-2 h-10 w-10 text-amber-200" aria-hidden />
              <p className="text-xl font-black uppercase tracking-wide text-amber-100">
                {isNoEligibleTerminalState
                  ? 'Матч завершен'
                  : spectatorHeroBustedOthersContinue
                    ? 'Стек на столе закончен'
                    : 'Матч завершен'}
              </p>
              <p className="mt-2 text-xs leading-relaxed text-amber-50/90">
                {isNoEligibleTerminalState
                  ? 'Никто не вошёл в следующую раздачу (включая Свару ×8): все участники пропустили вход.'
                  : spectatorHeroBustedOthersContinue
                    ? `Победитель раздачи: ${spectatorMatchWinner?.name || game.winner?.name || 'соперник'}. На столе у вас 0 фишек — вы не участвуете в раздачах, пока не докупитесь. У соперников ещё есть стек, матч между ними может продолжиться.`
                    : `Победил ${spectatorMatchWinner?.name || 'соперник'}. Вы были наблюдателем до конца матча.`}
              </p>
              <p className="mt-1 text-[11px] text-amber-200/85">
                Чтобы снова играть за столом, нужно минимум{' '}
                <span className="font-mono font-black text-amber-100 drop-shadow-[0_0_10px_rgba(251,191,36,0.45)]">
                  {spectatorReentryShortage.toLocaleString('ru-RU')} SEKA
                </span>{' '}
                на стол (с учётом анте).
              </p>
              <div className="mt-5 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={startFreshSession}
                  disabled={!canStartFreshSession}
                  className={cn(
                    'inline-flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-bold transition',
                    canStartFreshSession
                      ? 'border-neon-cyan/45 bg-neon-cyan/15 text-neon-cyan hover:bg-neon-cyan/25'
                      : 'cursor-not-allowed border-border/70 bg-card/70 text-muted-foreground'
                  )}
                >
                  <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                  Начать новую игру
                </button>
                <button
                  type="button"
                  onClick={() => {
                    play('restart-game-button', 0.95);
                    handleSpectatorReenterClick();
                  }}
                  disabled={!canSpectatorReenter}
                  className={cn(
                    'inline-flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-bold transition',
                    canSpectatorReenter
                      ? 'border-emerald-300/55 bg-emerald-500/15 text-emerald-100 hover:bg-emerald-500/25'
                      : 'cursor-not-allowed border-border/70 bg-card/70 text-muted-foreground'
                  )}
                >
                  <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center">
                    <img src="/fiski.png" alt="" className="h-4 w-4 object-contain" draggable={false} />
                  </span>
                  <span className="flex flex-col items-start leading-tight normal-case tracking-normal">
                    <span className="text-[10px] font-bold uppercase tracking-wide">Докупка</span>
                    <span className="font-mono text-[11px] font-black text-emerald-100 drop-shadow-[0_0_10px_rgba(110,231,183,0.35)]">
                      {spectatorReentryShortage.toLocaleString('ru-RU')} SEKA
                    </span>
                    <span className="text-[10px] font-semibold text-emerald-100/85">чтобы продолжить</span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setShowMatchHistoryModal(true)}
                  className={cn(
                    'col-span-2 inline-flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-bold transition',
                    'border-amber-300/55 bg-amber-500/12 text-amber-100 hover:bg-amber-500/22'
                  )}
                >
                  <History className="h-3.5 w-3.5" aria-hidden />
                  Смотреть историю ходов
                </button>
                {canContinueWatchingBots ? (
                  <button
                    type="button"
                    onClick={handleContinueWatchingAfterBust}
                    className="col-span-2 inline-flex items-center justify-center gap-1.5 rounded-lg border border-cyan-300/55 bg-cyan-500/12 px-3 py-2 text-xs font-bold text-cyan-100 transition hover:bg-cyan-500/22"
                  >
                    <Eye className="h-3.5 w-3.5" aria-hidden />
                    Продолжить наблюдать
                  </button>
                ) : null}
              </div>
              </div>
            </div>
          </div>
        )}

        {showMatchHistoryModal && (matchHistoryRows.length > 0 || matchChronicle.length > 0) && (
          <div className="fixed inset-0 z-[66] overflow-y-auto overscroll-contain bg-black/55">
            <div className="flex min-h-[100dvh] w-full items-center justify-center p-3 py-3 sm:p-4">
              <div className="pointer-events-auto w-full max-w-[min(94vw,560px)] min-h-0 max-h-[min(85dvh,calc(100dvh-1.5rem))] overflow-y-auto rounded-2xl border border-amber-300/45 bg-gradient-to-b from-[#22170a]/95 to-[#120c07]/95 px-4 py-4">
              <p className="text-center text-lg font-black uppercase tracking-wide text-amber-100">
                История ходов
              </p>
              <p className="mt-1 text-center text-[11px] text-amber-200/85">
                Кто как ходил и сколько выиграл/проиграл по каждой раздаче
              </p>
              {matchChronicle.length > 0 ? (
                <div className="mt-3 max-h-[30vh] overflow-y-auto rounded-xl border border-amber-400/35 bg-black/25 px-2 py-2">
                  <p className="text-center text-[10px] font-bold uppercase tracking-wide text-amber-200/95">
                    Хроника матча
                  </p>
                  <p className="mt-0.5 text-center text-[9px] text-amber-200/70">
                    Свара и раздачи по порядку — стрелки между ходами одной улицы
                  </p>
                  <div className="mt-2 space-y-2.5">
                    {matchChronicle.map((entry) => (
                      <div
                        key={entry.id}
                        className="rounded-lg border border-white/10 bg-black/35 px-2 py-1.5 text-left"
                      >
                        <p className="text-[11px] font-bold text-amber-100">{entry.title}</p>
                        {entry.subtitle ? (
                          <p className="mt-0.5 text-[10px] text-amber-200/85">{entry.subtitle}</p>
                        ) : null}
                        {entry.kind === 'hand' ? (
                          <MatchChronicleChain steps={entry.steps} />
                        ) : (
                          <MatchChronicleVertical steps={entry.steps} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              <div className="mt-3 grid grid-cols-3 gap-1.5">
                {[
                  { id: 'all', label: 'Все' },
                  { id: 'hero', label: 'Только мои' },
                  { id: 'big', label: 'Крупные банки' },
                ].map((f) => (
                  <button
                    key={`history-filter-${f.id}`}
                    type="button"
                    onClick={() => setHistoryFilter(f.id)}
                    className={cn(
                      'inline-flex h-8 items-center justify-center rounded-lg border text-[10px] font-bold transition',
                      historyFilter === f.id
                        ? 'border-amber-300/70 bg-amber-500/20 text-amber-100'
                        : 'border-white/15 bg-black/25 text-amber-200/85 hover:bg-black/35'
                    )}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => setShowMatchHistoryModal(false)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 bg-black/30 px-2.5 py-1.5 text-[10px] font-bold text-amber-100 transition hover:bg-black/45"
                >
                  <ArrowLeft className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  Назад
                </button>
                <button
                  type="button"
                  onClick={handleCopyFullMatchHistory}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[10px] font-bold transition',
                    matchHistoryCopyFeedback
                      ? 'border-emerald-400/60 bg-emerald-500/20 text-emerald-100'
                      : 'border-amber-400/45 bg-amber-500/12 text-amber-100 hover:bg-amber-500/22'
                  )}
                >
                  <Copy className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  {matchHistoryCopyFeedback ? 'Скопировано в буфер' : 'Копировать всю историю'}
                </button>
              </div>
              <div className="mt-3 max-h-[58vh] space-y-2 overflow-y-auto pr-1">
                {filteredMatchHistoryRows.length > 0 ? (
                  filteredMatchHistoryRows.map((row, idx) => (
                    <div key={row.id} className="rounded-xl border border-white/10 bg-black/25 px-3 py-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-[11px] font-bold text-amber-100">
                          Раздача #{row.handIndex} · Банк {row.pot.toLocaleString('ru-RU')} SEKA
                        </p>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="rounded border border-white/15 bg-black/25 px-1.5 py-0.5 text-[10px] text-amber-200">
                            {row.endType}
                          </span>
                          {idx === 0 ? (
                            <button
                              type="button"
                              onClick={handleCopyFullMatchHistory}
                              className={cn(
                                'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border transition',
                                matchHistoryCopyFeedback
                                  ? 'border-emerald-400/55 bg-emerald-500/20 text-emerald-100'
                                  : 'border-amber-400/45 bg-black/30 text-amber-100 hover:bg-amber-500/15'
                              )}
                              title="Копировать всю историю матча (хроника, раздачи, карты, ходы)"
                              aria-label="Копировать всю историю матча"
                            >
                              <Copy className="h-3.5 w-3.5" aria-hidden />
                            </button>
                          ) : null}
                        </div>
                      </div>
                      <p className="mt-1 text-[10px] text-amber-100/90">Победитель: {row.winnerNames}</p>
                      <p className="mt-1 text-[11px]">
                        Ваш итог:{' '}
                        <span
                          className={cn(
                            'font-mono font-black',
                            row.heroDelta > 0 && 'text-emerald-200',
                            row.heroDelta < 0 && 'text-red-200',
                            row.heroDelta === 0 && 'text-white/80'
                          )}
                        >
                          {row.heroDelta > 0 ? '+' : ''}
                          {row.heroDelta.toLocaleString('ru-RU')} SEKA
                        </span>
                      </p>
                      {row.timestamp ? (
                        <p className="mt-0.5 text-[10px] text-amber-200/70">
                          {row.timestamp.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      ) : null}
                      {row.playerSnapshots && row.playerSnapshots.length > 0 ? (
                        <div className="mt-2 rounded-lg border border-cyan-500/25 bg-black/25 px-2 py-1.5">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-cyan-100/90">
                            Карты и комбинации
                          </p>
                          <p className="mt-0.5 text-[9px] leading-snug text-cyan-200/75">
                            Снимок стола из движка: все места, кому какие карты достались в этой раздаче
                          </p>
                          <div className="mt-2 space-y-2">
                            {row.playerSnapshots.map((ps) => (
                              <div
                                key={ps.key}
                                className={cn(
                                  'rounded-lg border px-2 py-1.5',
                                  ps.isWinner
                                    ? 'border-emerald-500/45 bg-emerald-950/35'
                                    : 'border-white/10 bg-black/35'
                                )}
                              >
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                  <span className="text-[10px] font-bold text-white/95">
                                    {ps.isHero ? 'Вы · ' : null}
                                    {ps.name}
                                    <span className="ml-1 font-mono text-[9px] font-normal text-white/45">
                                      м.{ps.seatIndex + 1}
                                    </span>
                                  </span>
                                  {ps.isWinner ? (
                                    <span className="rounded bg-emerald-500/25 px-1 py-0 text-[9px] font-bold uppercase text-emerald-100">
                                      победа
                                    </span>
                                  ) : null}
                                  {ps.isFolded ? (
                                    <span className="rounded bg-white/10 px-1 py-0 text-[9px] text-white/65">
                                      сброс
                                    </span>
                                  ) : null}
                                </div>
                                {ps.handDescription ? (
                                  <p className="mt-0.5 text-[9px] leading-snug text-amber-200/90">
                                    {ps.handDescription}
                                  </p>
                                ) : null}
                                <div className="mt-1 flex flex-wrap justify-center gap-0.5">
                                  {ps.cardsUi.length > 0 ? (
                                    ps.cardsUi.map((c, ci) => (
                                      <PlayingCard
                                        key={`${ps.key}-c${ci}`}
                                        suit={c.suit}
                                        rank={c.rank}
                                        size="sm"
                                      />
                                    ))
                                  ) : (
                                    <span className="text-[9px] text-white/45">нет карт в записи</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}
                      <div className="mt-2 rounded-lg border border-white/10 bg-black/20 px-2 py-1.5">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-100/85">Баланс</p>
                        <div className="mt-1 grid gap-1 sm:grid-cols-2">
                          {row.deltas.map((d) => (
                            <div key={`${row.id}-d-${d.playerId}`} className="flex items-center justify-between gap-2 rounded bg-black/25 px-2 py-1">
                              <span className="truncate text-[10px] text-white/90">{d.playerName}</span>
                              <span
                                className={cn(
                                  'font-mono text-[10px] font-bold',
                                  d.delta > 0 && 'text-emerald-200',
                                  d.delta < 0 && 'text-red-200',
                                  d.delta === 0 && 'text-white/75'
                                )}
                              >
                                {d.delta > 0 ? '+' : ''}
                                {Number(d.delta || 0).toLocaleString('ru-RU')} SEKA
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="mt-2 rounded-lg border border-white/10 bg-black/20 px-2 py-1.5">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-100/85">Ходы</p>
                        {row.actions.length > 0 ? (
                          <>
                            <MatchChronicleChain
                              steps={row.actions.map(
                                (a, actionIdx) =>
                                  `${actionIdx + 1}. ${a.playerName}: ${formatHistoryAction(a)}`
                              )}
                            />
                            <ol className="mt-2 space-y-1 border-t border-white/5 pt-2">
                              {row.actions.map((a, actionIdx) => (
                                <li
                                  key={`${row.id}-a-${actionIdx}`}
                                  className="flex items-center justify-between gap-2 rounded bg-black/25 px-2 py-1 text-[10px]"
                                >
                                  <span className="truncate text-white/90">
                                    {actionIdx + 1}. {a.playerName}
                                  </span>
                                  <span className="text-amber-100">{formatHistoryAction(a)}</span>
                                </li>
                              ))}
                            </ol>
                          </>
                        ) : (
                          <p className="mt-1 text-[10px] text-white/70">
                            Действия не записаны (авто-завершение или момент до старта торговли).
                          </p>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="rounded-xl border border-white/10 bg-black/25 px-3 py-3 text-center">
                    <p className="text-sm font-semibold text-amber-100">
                      {matchHistoryRows.length > 0 ? 'По выбранному фильтру нет раздач' : 'История пока пуста'}
                    </p>
                    <p className="mt-1 text-[11px] text-amber-200/80">
                      {matchHistoryRows.length > 0
                        ? 'Смените фильтр, чтобы увидеть другие раздачи.'
                        : 'Сыграйте новую раздачу, и здесь появятся ходы и баланс по игрокам.'}
                    </p>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setShowMatchHistoryModal(false)}
                className="mt-3 inline-flex h-10 w-full items-center justify-center rounded-lg border border-amber-300/45 bg-amber-500/10 text-xs font-bold text-amber-100 transition hover:bg-amber-500/20"
              >
                Закрыть
              </button>
              </div>
            </div>
          </div>
        )}

        {localStingerPayload && !localStingerClosed ? (
          <SessionResultOverlay
            key={localStingerPayload.outcomeId}
            outcome={localStingerPayload}
            autoDismiss={false}
            onClose={() => {
              if (winnerFanfareAfterStingerCloseRef.current) {
                winnerFanfareAfterStingerCloseRef.current = false;
                initAudio();
                setTimeout(() => play('winner-game', 1), 120);
              }
              setLocalStingerClosed(true);
            }}
          />
        ) : null}

        {game.gameEnded &&
          outcomeKind &&
          !isAbsoluteVictory &&
          !isSpectatorMatchOver &&
          !isNoEligibleTerminalState &&
          (!localStingerPayload || localStingerClosed) && (
          <div
            className="fixed inset-0 z-[60] overflow-y-auto overscroll-contain bg-black/25"
            aria-live="polite"
          >
            <div className="flex min-h-[100dvh] w-full items-center justify-center p-3 py-3 sm:p-4">
              <div
                className={cn(
                  'pointer-events-auto w-full max-w-[min(94vw,560px)] min-h-0 overflow-y-auto rounded-2xl px-4 py-4 text-center sm:px-6 sm:py-5',
                  'max-h-[min(85dvh,calc(100dvh-1.5rem))]',
                  outcomeKind === 'win' &&
                    'poker-fx-win-panel border border-emerald-400/50 bg-gradient-to-b from-emerald-950/95 to-[#0a1520]/95 shadow-[0_0_60px_rgba(52,211,153,0.25)]',
                  outcomeKind === 'lose' &&
                    'poker-fx-loss-panel border border-red-500/45 bg-gradient-to-b from-red-950/90 to-[#0a1520]/95 shadow-[0_0_40px_rgba(248,113,113,0.2)]',
                  outcomeKind === 'chop' &&
                    'border border-amber-400/45 bg-gradient-to-b from-amber-950/90 to-[#0a1520]/95 shadow-lg'
                )}
              >
              {outcomeKind === 'win' && (
                <>
                  <Trophy className="mx-auto mb-1.5 h-9 w-9 text-emerald-300 sm:h-10 sm:w-10" aria-hidden />
                  <p className="text-lg font-black uppercase tracking-wide text-emerald-100 sm:text-xl">Победа</p>
                </>
              )}
              {outcomeKind === 'lose' && (
                <>
                  <Skull className="mx-auto mb-1.5 h-9 w-9 text-red-300 sm:h-10 sm:w-10" aria-hidden />
                  <p className="text-lg font-black uppercase tracking-wide text-red-100 sm:text-xl">Поражение</p>
                </>
              )}
              {outcomeKind === 'chop' && (
                <>
                  <Sparkles className="mx-auto mb-1.5 h-9 w-9 text-amber-200 sm:h-10 sm:w-10" aria-hidden />
                  <p className="text-lg font-black uppercase tracking-wide text-amber-100 sm:text-xl">Ничья</p>
                </>
              )}
              {(winnerSummary || outcomeModalHistorySnapshot) && (
                <div className="mt-3 w-full rounded-lg border border-white/12 bg-black/35 px-3 py-2.5 text-left text-[11px] leading-snug text-white/90 shadow-inner">
                  {outcomeModalHistorySnapshot && outcomeModalHistorySnapshot.pot > 0 ? (
                    <p className="font-mono text-[12px] font-bold tabular-nums text-amber-100/95">
                      Банк раздачи: {outcomeModalHistorySnapshot.pot.toLocaleString('ru-RU')} SEKA
                    </p>
                  ) : null}
                  {winnerSummary ? (
                    <p
                      className={cn(
                        'text-white/88',
                        outcomeModalHistorySnapshot && outcomeModalHistorySnapshot.pot > 0 && 'mt-1'
                      )}
                    >
                      {winnerSummary}
                    </p>
                  ) : outcomeModalHistorySnapshot?.winnerNames ? (
                    <p
                      className={cn(
                        'text-white/88',
                        outcomeModalHistorySnapshot && outcomeModalHistorySnapshot.pot > 0 && 'mt-1'
                      )}
                    >
                      {game.winner
                        ? `Победитель: ${outcomeModalHistorySnapshot.winnerNames}`
                        : game.winners && game.winners.length > 1
                          ? `Ничья: ${outcomeModalHistorySnapshot.winnerNames}`
                          : `Итог: ${outcomeModalHistorySnapshot.winnerNames}`}
                    </p>
                  ) : null}
                </div>
              )}
              <p className="mt-2 max-w-md px-1 text-center text-[10px] leading-snug text-white/55">
                Изменение вашего стека за эту раздачу — с учётом анте и всех взносов в торгах (по записям раздачи).
              </p>
              {heroOutcomeDisplayDelta != null && (
                <p
                  className={cn(
                    'mt-0.5 font-mono text-lg font-black tabular-nums tracking-tight',
                    outcomeKind === 'win' &&
                      heroOutcomeDisplayDelta > 0 &&
                      'text-emerald-300 drop-shadow-[0_0_12px_rgba(52,211,153,0.35)]',
                    outcomeKind === 'win' && heroOutcomeDisplayDelta <= 0 && 'text-emerald-200/85',
                    outcomeKind === 'lose' &&
                      heroOutcomeDisplayDelta < 0 &&
                      'text-red-300 drop-shadow-[0_0_12px_rgba(248,113,113,0.3)]',
                    outcomeKind === 'lose' && heroOutcomeDisplayDelta >= 0 && 'text-red-200/85',
                    outcomeKind === 'chop' && heroOutcomeDisplayDelta > 0 && 'text-emerald-300/90',
                    outcomeKind === 'chop' && heroOutcomeDisplayDelta < 0 && 'text-red-300/90',
                    outcomeKind === 'chop' && heroOutcomeDisplayDelta === 0 && 'text-amber-200/90'
                  )}
                >
                  {heroOutcomeDisplayDelta > 0 ? '+' : ''}
                  {heroOutcomeDisplayDelta.toLocaleString('ru-RU')} SEKA
                </p>
              )}
              {outcomeModalHistorySnapshot && outcomeModalHistorySnapshot.sortedDeltas.length > 0 ? (
                <div className="mt-3 w-full max-w-md rounded-lg border border-white/10 bg-black/25 px-2 py-2 text-left">
                  <p className="text-center text-[10px] font-semibold uppercase tracking-wide text-white/60">
                    Игроки после раздачи
                  </p>
                  <ul className="mt-1.5 space-y-1">
                    {outcomeModalHistorySnapshot.sortedDeltas.map((d) => {
                      const pid = Number(d.playerId);
                      const delta = Number(d.delta || 0);
                      const end = Number(d.endChips ?? 0);
                      return (
                        <li
                          key={`out-delta-${pid}-${d.playerName}`}
                          className="flex items-center gap-2 rounded-md border border-white/8 bg-black/30 px-2 py-1 text-[10px]"
                        >
                          <span
                            className={cn(
                              'min-w-0 flex-1 truncate font-medium',
                              pid === 1 ? 'text-cyan-100' : 'text-white/85'
                            )}
                          >
                            {d.playerName || '—'}
                            {pid === 1 ? (
                              <span className="ml-1 text-[9px] font-normal text-cyan-200/80">(вы)</span>
                            ) : null}
                          </span>
                          <span
                            className={cn(
                              'shrink-0 font-mono text-[10px] font-bold tabular-nums',
                              delta > 0 && 'text-emerald-300',
                              delta < 0 && 'text-red-300/95',
                              delta === 0 && 'text-white/45'
                            )}
                          >
                            {delta > 0 ? '+' : ''}
                            {delta.toLocaleString('ru-RU')}
                          </span>
                          <span className="shrink-0 font-mono text-[9px] tabular-nums text-white/40" title="Стек на столе">
                            → {end.toLocaleString('ru-RU')}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
              <div className="mt-4 flex w-full flex-wrap items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={onBack}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 bg-black/30 px-2.5 py-1.5 text-[10px] font-bold text-amber-100 transition hover:bg-black/45"
                >
                  <ArrowLeft className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  Назад
                </button>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleCopyOutcomePanel}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[10px] font-bold transition',
                      outcomeCopyFeedback
                        ? 'border-emerald-400/60 bg-emerald-500/20 text-emerald-100'
                        : outcomeKind === 'win'
                          ? 'border-emerald-400/45 bg-emerald-500/12 text-emerald-100 hover:bg-emerald-500/22'
                          : outcomeKind === 'lose'
                            ? 'border-red-400/45 bg-red-500/12 text-red-100 hover:bg-red-500/22'
                            : 'border-amber-400/45 bg-amber-500/12 text-amber-100 hover:bg-amber-500/22'
                    )}
                  >
                    <Copy className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    {outcomeCopyFeedback ? 'Снимок скопирован' : 'Копировать снимок (хроника + раздача)'}
                  </button>
                </div>
              </div>
              {(matchChronicle.length > 0 ||
                Boolean(outcomeShowdownPanel) ||
                outcomePanelSnapshots.length > 0 ||
                outcomeModalHandActions.length > 0 ||
                matchHistoryRows.length > 0) && (
                <button
                  type="button"
                  onClick={() => setOutcomeDetailsExpanded((v) => !v)}
                  aria-expanded={outcomeDetailsExpanded}
                  className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-white/15 bg-black/25 px-3 py-2 text-[11px] font-bold text-white/90 transition hover:bg-black/35"
                >
                  {outcomeDetailsExpanded ? (
                    <>
                      <ChevronUp className="h-4 w-4 shrink-0" aria-hidden />
                      Скрыть детали и историю
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
                      Показать детали и историю
                    </>
                  )}
                </button>
              )}
              {outcomeDetailsExpanded ? (
                <>
                  {(matchHistoryRows.length > 0 || matchChronicle.length > 0) && (
                    <button
                      type="button"
                      onClick={() => setShowMatchHistoryModal(true)}
                      className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-amber-400/45 bg-amber-500/10 px-3 py-2 text-[10px] font-bold text-amber-100 transition hover:bg-amber-500/20"
                    >
                      <History className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      Полная история матча
                    </button>
                  )}
                  {matchChronicle.length > 0 ? (
                <div className="mt-3 w-full max-h-[20vh] overflow-y-auto rounded-lg border border-amber-500/25 bg-black/25 px-2 py-2 text-left">
                  <p className="text-center text-[10px] font-bold uppercase tracking-wide text-amber-200/90">
                    Недавние события
                  </p>
                  <div className="mt-1.5 space-y-1.5">
                    {matchChronicle.slice(-6).map((e) => (
                      <div key={e.id} className="rounded border border-white/10 bg-black/30 px-2 py-1 text-[9px] text-amber-100/90">
                        <span className="font-semibold text-amber-50">{e.title}</span>
                        {e.subtitle ? (
                          <span className="ml-1 text-amber-200/75">{e.subtitle}</span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {outcomeShowdownPanel && (
                <div
                  className={cn(
                    'mt-4 max-h-[36vh] w-full overflow-y-auto rounded-lg border bg-black/20 px-2 py-2 text-left',
                    outcomeShowdownPanel.variant === 'lose' && 'border-red-500/25',
                    outcomeShowdownPanel.variant === 'win' && 'border-emerald-500/30',
                    outcomeShowdownPanel.variant === 'chop' && 'border-amber-500/30'
                  )}
                >
                  <p
                    className={cn(
                      'mb-2 text-center text-[11px] font-semibold uppercase tracking-wide',
                      outcomeShowdownPanel.variant === 'lose' && 'text-red-200/90',
                      outcomeShowdownPanel.variant === 'win' && 'text-emerald-200/90',
                      outcomeShowdownPanel.variant === 'chop' && 'text-amber-200/90'
                    )}
                  >
                    Разбор раздачи
                  </p>
                  <ul className="space-y-2">
                    {outcomeShowdownPanel.rows.map((row) => (
                      <li
                        key={row.key}
                        className={cn(
                          'rounded-xl px-2.5 py-2.5 text-left',
                          row.isWinner
                            ? 'border border-emerald-400/70 bg-gradient-to-br from-emerald-500/35 via-emerald-500/20 to-emerald-600/10 shadow-[0_0_32px_rgba(52,211,153,0.45)] ring-1 ring-emerald-300/35'
                            : 'border border-red-500/50 bg-gradient-to-br from-red-950/70 to-red-950/40 shadow-[inset_0_0_28px_rgba(248,113,113,0.14)] ring-1 ring-red-500/25'
                        )}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span
                              className={cn(
                                'text-sm font-bold',
                                row.isWinner ? 'text-emerald-50' : 'text-red-100'
                              )}
                            >
                              {row.nameShort}
                            </span>
                            {row.isHero && (
                              <span
                                className={cn(
                                  'rounded px-1 py-0 text-[10px] font-semibold',
                                  row.isWinner
                                    ? 'bg-emerald-400/25 text-emerald-50'
                                    : 'bg-red-500/25 text-red-100'
                                )}
                              >
                                Вы
                              </span>
                            )}
                            {row.isWinner && (
                              <span className="rounded bg-emerald-300/35 px-1.5 py-0 text-[10px] font-bold uppercase tracking-wide text-emerald-50 shadow-[0_0_12px_rgba(167,243,208,0.45)]">
                                Победитель
                              </span>
                            )}
                            {row.allInAnte && (
                              <span
                                className={cn(
                                  'rounded px-1 py-0 text-[9px] font-bold uppercase',
                                  row.isWinner ? 'bg-amber-400/25 text-amber-100' : 'bg-amber-500/20 text-amber-200/90'
                                )}
                              >
                                All-in анте
                              </span>
                            )}
                            {row.folded && (
                              <span
                                className={cn(
                                  'text-[10px]',
                                  row.isWinner ? 'text-emerald-200/80' : 'text-red-300/85'
                                )}
                              >
                                {row.didNotEnter ? 'Не вошел в раздачу' : 'Сбросил'}
                              </span>
                            )}
                          </div>
                          {row.chipsDelta != null && (
                            <span
                              className={cn(
                                'shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide',
                                row.chipsDelta > 0 &&
                                  'border-emerald-200/60 bg-emerald-300/30 text-emerald-50 shadow-[0_0_16px_rgba(52,211,153,0.45)]',
                                row.chipsDelta < 0 &&
                                  'border-red-300/60 bg-red-500/30 text-red-50 shadow-[0_0_16px_rgba(248,113,113,0.4)]',
                                row.chipsDelta === 0 &&
                                  'border-white/35 bg-white/10 text-white/90 shadow-[0_0_12px_rgba(255,255,255,0.2)]'
                              )}
                            >
                              {row.chipsDelta > 0 ? '+' : ''}
                              {row.chipsDelta.toLocaleString('ru-RU')} SEKA
                            </span>
                          )}
                        </div>
                        <p
                          className={cn(
                            'mt-0.5 text-[11px] leading-snug',
                            row.isWinner ? 'text-emerald-100/90' : 'text-red-200/80'
                          )}
                        >
                          {row.description}
                        </p>
                        <div className="mt-1.5 flex justify-center gap-0.5">
                          {row.cards.map((c, i) => (
                            <PlayingCard key={`${row.key}-c${i}`} suit={c.suit} rank={c.rank} size="sm" />
                          ))}
                        </div>
                      </li>
                    ))}
                  </ul>
                  {outcomeShowdownPanel.explain ? (
                    <p
                      className={cn(
                        'mt-3 border-t pt-2 text-xs leading-snug',
                        outcomeShowdownPanel.variant === 'lose' && 'border-red-500/20 text-red-100/90',
                        outcomeShowdownPanel.variant === 'win' && 'border-emerald-500/25 text-emerald-100/90',
                        outcomeShowdownPanel.variant === 'chop' && 'border-amber-500/25 text-amber-100/90'
                      )}
                    >
                      {outcomeShowdownPanel.explain}
                    </p>
                  ) : null}
                </div>
              )}
              {outcomePanelSnapshots.length > 0 ? (
                <div
                  className={cn(
                    'mt-3 w-full rounded-lg border px-2 py-2 text-left',
                    outcomeKind === 'win' && 'border-cyan-500/30 bg-black/25',
                    outcomeKind === 'lose' && 'border-cyan-600/25 bg-black/25',
                    outcomeKind === 'chop' && 'border-cyan-500/25 bg-black/25'
                  )}
                >
                  <p className="text-center text-[10px] font-semibold uppercase tracking-wide text-cyan-100/90">
                    Запись движка — все места
                  </p>
                  <p className="mt-0.5 text-center text-[9px] leading-snug text-cyan-200/75">
                    Карты и комбинации из gameHistory (как в полной истории матча)
                  </p>
                  <div className="mt-2 max-h-[28vh] space-y-2 overflow-y-auto">
                    {outcomePanelSnapshots.map((ps) => (
                      <div
                        key={`out-snap-${ps.key}`}
                        className={cn(
                          'rounded-lg border px-2 py-1.5',
                          ps.isWinner
                            ? 'border-emerald-500/45 bg-emerald-950/30'
                            : 'border-white/10 bg-black/35'
                        )}
                      >
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          <span className="text-[10px] font-bold text-white/95">
                            {ps.isHero ? 'Вы · ' : null}
                            {ps.name}
                            <span className="ml-1 font-mono text-[9px] font-normal text-white/45">
                              м.{ps.seatIndex + 1}
                            </span>
                          </span>
                          {ps.isWinner ? (
                            <span className="rounded bg-emerald-500/25 px-1 py-0 text-[9px] font-bold uppercase text-emerald-100">
                              победа
                            </span>
                          ) : null}
                          {ps.isFolded ? (
                            <span className="rounded bg-white/10 px-1 py-0 text-[9px] text-white/65">
                              сброс
                            </span>
                          ) : null}
                        </div>
                        {ps.handDescription ? (
                          <p className="mt-0.5 text-[9px] text-amber-200/90">{ps.handDescription}</p>
                        ) : null}
                        <div className="mt-1 flex flex-wrap justify-center gap-0.5">
                          {ps.cardsUi.length > 0 ? (
                            ps.cardsUi.map((c, ci) => (
                              <PlayingCard
                                key={`out-ps-${ps.key}-c${ci}`}
                                suit={c.suit}
                                rank={c.rank}
                                size="sm"
                              />
                            ))
                          ) : (
                            <span className="text-[9px] text-white/45">нет карт</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              <div
                className={cn(
                  'mt-3 max-h-[min(32vh,260px)] w-full overflow-y-auto rounded-lg border px-2 py-2 text-left',
                  outcomeKind === 'win' && 'border-emerald-500/30 bg-black/20',
                  outcomeKind === 'lose' && 'border-red-500/30 bg-black/20',
                  outcomeKind === 'chop' && 'border-amber-500/30 bg-black/20'
                )}
              >
                <p
                  className={cn(
                    'text-center text-[10px] font-semibold uppercase tracking-wide',
                    outcomeKind === 'win' && 'text-emerald-200/90',
                    outcomeKind === 'lose' && 'text-red-200/90',
                    outcomeKind === 'chop' && 'text-amber-200/90'
                  )}
                >
                  История ходов
                </p>
                <p className="mt-0.5 text-center text-[9px] text-white/55">
                  Порядок со стрелками — как в окне полной истории
                </p>
                {outcomeModalHandActions.length > 0 ? (
                  <>
                    <MatchChronicleChain
                      steps={outcomeModalHandActions.map(
                        (a, actionIdx) =>
                          `${actionIdx + 1}. ${a?.playerName ?? '—'}: ${formatHistoryAction(a)}`
                      )}
                    />
                    <ol className="mt-2 space-y-1 border-t border-white/10 pt-2">
                      {outcomeModalHandActions.map((a, actionIdx) => (
                        <li
                          key={`outcome-act-${actionIdx}-${a?.playerId ?? 'x'}-${a?.action ?? ''}`}
                          className="flex items-center justify-between gap-2 rounded bg-black/30 px-2 py-1 text-[10px]"
                        >
                          <span className="truncate text-left text-white/90">
                            {actionIdx + 1}. {a?.playerName ?? '—'}
                          </span>
                          <span
                            className={cn(
                              'shrink-0',
                              outcomeKind === 'win' && 'text-emerald-100',
                              outcomeKind === 'lose' && 'text-red-100',
                              outcomeKind === 'chop' && 'text-amber-100'
                            )}
                          >
                            {formatHistoryAction(a)}
                          </span>
                        </li>
                      ))}
                    </ol>
                  </>
                ) : (
                  <p className="mt-2 text-center text-[10px] leading-snug text-white/65">
                    Действия не записаны (авто-завершение или момент до старта торговли).
                  </p>
                )}
              </div>
                </>
              ) : null}
              {!outcomeShowdownPanel && winnerSummary && (
                <p className="mt-3 text-base font-bold leading-snug text-white">{winnerSummary}</p>
              )}
              {!outcomeShowdownPanel && game.winner && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {game.evaluateHand(game.winner.cards).description}
                </p>
              )}
              <button
                type="button"
                onClick={handleStartNextHandClick}
                className="mt-6 inline-flex min-w-[180px] items-center justify-center rounded-lg border border-neon-cyan/45 bg-neon-cyan/15 px-3 py-2 text-xs font-bold text-neon-cyan transition hover:bg-neon-cyan/25"
              >
                Новая раздача
              </button>
              </div>
            </div>
          </div>
        )}

        <div
          ref={seatArenaRef}
          className={cn(
            'relative mx-auto w-full flex-1',
            seatArena.maxWidthClass,
            seatArena.minHeightClass,
            seatArrangeMode && 'rounded-xl ring-2 ring-amber-400/45 ring-offset-2 ring-offset-background'
          )}
        >
          <div className="absolute inset-0 flex items-center justify-center py-0.5">
            <PokerTableSurface
              ref={deckRef}
              potAnchorRef={potAnchorRef}
              communitySlots={EMPTY_COMMUNITY}
              pot={game.pot}
              potLabel="POT"
              showCommunity={false}
              showDeck
            />
          </div>
          <div className="pointer-events-none absolute inset-0 z-10">
            {seatSlots.map((slot) => {
              const seat = slot.seat;
              const p = game.players[seat];
              if (!p) return null;
              const uiCards = (p.cards || []).map(sekaCardToPlaying).filter(Boolean);
              const nm = p.name.length > 10 ? `${p.name.slice(0, 9)}…` : p.name;
              const showFace = shouldShowSeatCardFaces(seat, p, showCards, game.gameEnded);
              const isWinner = Boolean(game.gameEnded && game.winner && game.winner.id === p.id);
              const cardFaceDownByIndex = buildSeatCardFaceDown(
                seat,
                p,
                dealRevealed,
                showCards,
                game.gameEnded
              );
              const dealCardKeys = buildDealCardKeys(seat, dealEpoch, dealRevealed);
              const showCardByIndexSeat = buildShowCardByIndex(seat, dealRevealed);
              const isNearDeckSeat = seat === 3;
              const footerMode = isNearDeckSeat || seatDensity.compact ? 'minimal' : 'full';
              const footerLabel =
                !seatDensity.hideSecondaryMeta && p.isDark ? (seat === 0 ? 'В темную' : 'Бот втемную') : null;
              const modeIndicatorLabel = p.isDark ? 'В ТЕМНУЮ' : 'В ОТКРЫТУЮ';
              const modeIndicatorState = p.isDark ? 'dark' : 'open';
              const nameActionBadge =
                !seatDensity.hideLastActionBadges && lastSeatActions[seat]?.kind ? (
                  <SeatLastMoveBadge
                    action={lastSeatActions[seat]}
                    isDimmed={p.isFolded}
                    compact={footerMode === 'minimal'}
                  />
                ) : null;
              const cardsForSeat =
                seat === 0 && (heroSittingOut || (stripSeatUI === 0 && !game.gameEnded)) ? [] : uiCards;

              const disp = seatDisplayPercents(slot, seatOffsetBySeat[seat]);

              return (
                <div
                  key={seat}
                  className="absolute"
                  style={{
                    left: `${disp.leftPct}%`,
                    top: `${disp.topPct}%`,
                    transform: `translate(-50%, -50%) scale(${seatDensity.seatScale})`,
                    transformOrigin: 'center',
                  }}
                >
                  <div
                    className={cn(
                      'relative flex flex-col items-center gap-0.5',
                      seatArrangeMode
                        ? 'pointer-events-auto cursor-grab touch-none active:cursor-grabbing'
                        : 'pointer-events-auto'
                    )}
                    onPointerDown={(ev) => handleSeatDragPointerDown(ev, seat, slot.leftPct, slot.topPct)}
                    onPointerMove={handleSeatDragPointerMove}
                    onPointerUp={handleSeatDragPointerUp}
                    onPointerCancel={handleSeatDragPointerUp}
                    onLostPointerCapture={handleSeatDragLostCapture}
                  >
                    <IndustrialSeat
                      name={nm}
                      chips={p.chips}
                      chipsDisplay={seatDensity.hideSecondaryMeta ? undefined : p.chips}
                      anteSourceRef={anteSourceRefsRef.current[seat]}
                      bet={p.currentBet}
                      cards={cardsForSeat}
                      compact={seatDensity.compact}
                      isDealer={game.dealerIndex === seat}
                      isCurrentTurn={game.currentPlayer === seat && !game.gameEnded}
                      isFolded={p.isFolded}
                      isWinner={isWinner}
                      showCards={showFace}
                      cardFaceDownByIndex={cardFaceDownByIndex}
                      dealCardKeys={dealCardKeys}
                      cardSlotRefs={seatCardSlotRefsRef.current[seat]}
                      showCardByIndex={showCardByIndexSeat}
                      isUser={seat === 0}
                      isBot={seat !== 0}
                      suppressAvatarDragPropagation={seatArrangeMode}
                      onAvatarClick={() => {
                        if (seat === 0) {
                          setSeatProfileOpen({
                            variant: 'self',
                            displayName: p.name,
                            chips: p.chips,
                            initialAvatarUrl: heroAvatarUrl,
                            userId: user?.id != null ? String(user.id) : '',
                          });
                        } else {
                          setSeatProfileOpen({
                            variant: 'bot',
                            displayName: p.name,
                            chips: p.chips,
                          });
                        }
                      }}
                      avatarUrl={seat === 0 ? heroAvatarUrl : ''}
                      seatColorIndex={seat}
                      modeIndicatorLabel={modeIndicatorLabel}
                      modeIndicatorState={modeIndicatorState}
                      footerLabel={footerLabel}
                      nameActionBadge={nameActionBadge}
                      footerMode={footerMode}
                      turnTimeLeftSec={
                        game.currentPlayer === seat && seat === 0 && !game.gameEnded ? timeLeft : null
                      }
                      turnTimeTotalSec={seat === 0 ? SEKA_HERO_TURN_SECONDS_CEIL : null}
                      turnTimeFraction={
                        game.currentPlayer === seat &&
                        seat !== 0 &&
                        botTurnClock &&
                        botTurnClock.seat === seat
                          ? Math.max(
                              0,
                              Math.min(1, (botTurnClock.endAt - Date.now()) / botTurnClock.totalMs)
                            )
                          : undefined
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {game.currentBet > 0 && (
          <div className="text-center font-mono text-xs text-muted-foreground">
            Ставка: <span className="text-neon-orange">{game.currentBet} SEKA</span>
          </div>
        )}

        {game.sidePots && game.sidePots.length > 0 && (
          <div className="rounded-lg border border-border/60 bg-card/60 px-3 py-2 text-center text-[10px] text-muted-foreground">
            {game.sidePots.map((sp, i) => (
              <span key={i} className="block">
                Боковой банк {i + 1}: {sp.amount} SEKA
              </span>
            ))}
            <p className="mt-1.5 text-[9px] leading-snug text-muted-foreground/80">
              Сводка по уровням вклада (при неравных стеках; сейчас в движке поле зарезервировано).
            </p>
          </div>
        )}

        {(() => {
          if (game.gameEnded) return null;
          if (heroSittingOut) return null;
          /**
           * Локально полоса закреплена за героем (stripSeatUI). Раньше getStripSeat(currentPlayer)
           * при «все в темную» показывал бота снизу, а карты героя дублировались на месте 0.
           */
          const stripSeat = stripSeatUI;
          const p = game.players[stripSeat];
          if (!p?.cards?.length) return null;
          const show = shouldShowSeatCardFaces(stripSeat, p, showCards, false);
          const desc = show ? game.evaluateHand(p.cards).description : null;
          const stripFaceByIdx = buildSeatCardFaceDown(
            stripSeat,
            p,
            dealRevealed,
            showCards,
            false
          );
          const stripKeys = buildDealCardKeys(stripSeat, dealEpoch, dealRevealed);
          const stripShowCardByIndex = buildShowCardByIndex(stripSeat, dealRevealed);
          return (
            <PlayerHandStrip
              cards={(p.cards || []).map(sekaCardToPlaying).filter(Boolean)}
              handName={desc}
              faceDown={!show}
              faceDownByIndex={stripFaceByIdx}
              cardKeys={stripKeys}
              slotRefs={stripSeat === 0 ? heroStripSlotRefs.current : undefined}
              showCardByIndex={stripShowCardByIndex}
            />
          );
        })()}

        {!game.gameEnded && (
          <div className="sticky bottom-0 z-30 bg-gradient-to-t from-background via-background to-transparent pb-3 pt-1.5">
            {heroSittingOut ? (
              heroNeedsAnteHelp ? (
                <div className="mx-auto w-full max-w-lg px-2">
                  <div className="rounded-xl border border-amber-300/35 bg-amber-500/10 px-3 py-2 text-center">
                    <p className="text-[11px] text-amber-100/90">
                      В этой раздаче вы наблюдатель. Для входа в следующую руку нужна докупка от{' '}
                      {minBuyIn.toLocaleString('ru-RU')} SEKA (анте × свара: {effAnteNext.toLocaleString('ru-RU')}).
                    </p>
                    {heroPlayer?.pendingBuyIn > 0 ? (
                      <p className="mt-1 text-[10px] text-amber-200/90">
                        Докупка в очереди: {Number(heroPlayer.pendingBuyIn).toLocaleString('ru-RU')} SEKA (применится со
                        следующей руки)
                      </p>
                    ) : null}
                    <button
                      type="button"
                      onClick={openBuyInModal}
                      disabled={!canBuyIn}
                      className={cn(
                        'mt-2 h-11 w-full rounded-lg border text-xs font-semibold transition',
                        canBuyIn
                          ? 'border-emerald-300/55 bg-emerald-500/15 text-emerald-100 hover:bg-emerald-500/25'
                          : 'cursor-not-allowed border-border/70 bg-card/70 text-muted-foreground'
                      )}
                    >
                      {canBuyIn
                        ? `Докупиться (баланс ${userBalance.toLocaleString('ru-RU')})`
                        : `Недостаточно баланса (${userBalance.toLocaleString('ru-RU')})`}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mx-auto w-full max-w-lg px-2">
                  <div className="rounded-xl border border-slate-500/35 bg-slate-500/10 px-3 py-2 text-center">
                    <p className="text-[11px] text-slate-100/90">
                      Вы пропускаете эту раздачу (отказ от свара). На столе достаточно фишек для следующего анте — с
                      следующей раздачи вы снова в игре.
                    </p>
                  </div>
                </div>
              )
            ) : heroAllInContestingPot && game.gamePhase === 'preflop' ? (
              <div className="mx-auto w-full max-w-lg px-2">
                <div className="rounded-xl border border-cyan-300/35 bg-cyan-500/10 px-3 py-3 text-center">
                  <p className="text-xs font-semibold uppercase tracking-wide text-cyan-100">
                    Вы в олл-ине
                  </p>
                  <p className="mt-1 text-[11px] text-cyan-100/85">
                    Ожидание вскрытия — ходят соперники.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {showRaiseInput && raiseRange && clampedSekaRaise != null ? (
                  <div className="mx-auto w-full max-w-lg px-2">
                    <PokerBetRaiseSlider
                      min={raiseRange.min}
                      max={raiseRange.max}
                      value={clampedSekaRaise}
                      onChange={setSekaRaiseAmount}
                      className="pb-2"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setShowRaiseInput(false)}
                        className="flex h-12 items-center justify-center rounded-lg border border-border bg-card/80 text-xs font-semibold text-foreground hover:bg-muted/40"
                      >
                        Отмена
                      </button>
                      <button
                        type="button"
                        onClick={() => handlePlayerMove('RAISE', clampedSekaRaise)}
                        className="flex h-12 items-center justify-center rounded-lg border border-neon-orange/50 bg-neon-orange/15 text-xs font-semibold text-neon-orange hover:bg-neon-orange/25"
                      >
                        Поднять до {clampedSekaRaise.toLocaleString('ru-RU')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <SekaActionBar
                    actions={sekaBarActions}
                    onAction={onBarAction}
                    disabled={game.currentPlayer !== 0}
                  />
                )}
                {!showRaiseInput ? (
                  <div className="mt-2 w-full max-w-lg mx-auto px-1 sm:px-0">
                    <button
                      type="button"
                      onClick={() => setLiveMovesOpen((o) => !o)}
                      aria-expanded={liveMovesOpen}
                      className="inline-flex w-full flex-wrap items-center justify-center gap-x-2 gap-y-1 rounded-lg border border-white/12 bg-black/20 px-2 py-2 text-[10px] font-bold uppercase tracking-wide text-amber-100/95 transition hover:bg-black/30"
                    >
                      {liveMovesOpen ? (
                        <ChevronUp className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      )}
                      Ходы раздачи
                      {liveHandActionLog.length > 0 ? (
                        <span className="rounded-full border border-amber-400/35 bg-amber-500/15 px-1.5 py-0 text-[9px] font-mono tabular-nums text-amber-50">
                          {liveHandActionLog.length}
                        </span>
                      ) : null}
                      {!liveMovesOpen && liveHandActionLog.length > 0 ? (
                        <LiveSekaHandMovesTrail actions={liveHandActionLog} className="min-w-0 flex-1 basis-full justify-center sm:basis-auto sm:flex-initial" />
                      ) : null}
                    </button>
                    {liveMovesOpen ? (
                      <div className="mt-1.5 max-h-[min(40vh,280px)] overflow-y-auto rounded-lg border border-amber-500/25 bg-black/30 px-2 py-2 text-left">
                        <LiveSekaHandMovesPanel actions={liveHandActionLog} />
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </>
            )}
          </div>
        )}

        {showBuyInModal && heroNeedsAnteHelp && canBuyIn && (
          <div className="fixed inset-0 z-[65] overflow-y-auto overscroll-contain bg-black/45">
            <div className="flex min-h-[100dvh] w-full items-end justify-center p-3 sm:items-center sm:p-4">
              <div className="pointer-events-auto w-full max-w-md min-h-0 max-h-[min(85dvh,calc(100dvh-1.5rem))] overflow-y-auto rounded-2xl border border-emerald-300/45 bg-card/95 p-3 shadow-2xl">
              <p className="text-center text-sm font-semibold text-emerald-100">Докупка фишек</p>
              <p className="mt-1 text-center text-[11px] text-muted-foreground">
                Баланс: {userBalance.toLocaleString('ru-RU')} SEKA · Мин: {minBuyIn} SEKA
              </p>
              <div className="mt-2">
                <PokerBetRaiseSlider
                  min={minBuyIn}
                  max={maxBuyIn}
                  value={clampedBuyIn}
                  onChange={setBuyInAmount}
                  className="pb-2"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setShowBuyInModal(false)}
                  className="flex h-11 items-center justify-center rounded-lg border border-border bg-card/80 text-xs font-semibold text-foreground hover:bg-muted/40"
                >
                  Отмена
                </button>
                <button
                  type="button"
                  onClick={applyLocalBuyIn}
                  className="flex h-11 items-center justify-center rounded-lg border border-emerald-300/55 bg-emerald-500/15 text-xs font-semibold text-emerald-100 hover:bg-emerald-500/25"
                >
                  Докупить {clampedBuyIn.toLocaleString('ru-RU')}
                </button>
              </div>
              </div>
            </div>
          </div>
        )}

        {svarkaCommitOpen ? (
          <div className="fixed inset-0 z-[70] overflow-y-auto overscroll-contain bg-black/55">
            <div className="flex min-h-[100dvh] w-full items-center justify-center p-3 py-3 sm:p-4">
              <div className="pointer-events-auto w-full max-w-md min-h-0 max-h-[min(85dvh,calc(100dvh-1.5rem))] overflow-y-auto animate-[pulse_1.2s_ease-in-out_infinite] rounded-2xl border border-amber-400/70 bg-gradient-to-b from-amber-950/90 to-slate-950/95 p-4 shadow-[0_0_40px_rgba(251,191,36,0.35)]">
              <p className="text-center text-lg font-bold uppercase tracking-wide text-amber-100 drop-shadow-[0_0_14px_rgba(251,191,36,0.5)]">
                Свара ×{svarkaCommitMeta.mult}!
              </p>
              <p className="mt-2 text-center text-sm text-amber-100/90">
                Номинальный вход:{' '}
                <span className="font-mono font-semibold text-white">
                  {svarkaCommitMeta.eff.toLocaleString('ru-RU')} фишек
                </span>
              </p>
              {heroSvarkaSeat?.canParticipate ? (
                <p className="mt-1 text-center text-[11px] text-amber-200/85">
                  С вашего стека спишется:{' '}
                  <span className="font-mono font-bold text-amber-50">
                    {Math.min(svarkaCommitMeta.eff, Number(game.players?.[0]?.chips ?? 0)).toLocaleString('ru-RU')}
                  </span>
                  {Math.min(svarkaCommitMeta.eff, Number(game.players?.[0]?.chips ?? 0)) < svarkaCommitMeta.eff
                    ? ' (all-in к полному анте)'
                    : ''}
                </p>
              ) : null}
              <p className="mt-2 text-center text-[11px] text-amber-200/75">
                {svarkaCommitPhase === 'collecting'
                  ? `Без выбора через ${svarkaCommitSeconds} с — пропуск раздачи`
                  : 'Решения приняты · показываем результаты…'}
              </p>
              {svarkaCommitPhase === 'collecting' ? (
                <div className="mt-2">
                  <div className="h-1.5 w-full overflow-hidden rounded-full border border-white/15 bg-black/40">
                    <div
                      className={cn(
                        'h-full rounded-full bg-gradient-to-r transition-[filter] duration-700 ease-linear',
                        svarkaCountdownShouldAnimateWidth
                          ? 'transition-[width,filter] duration-700 ease-linear'
                          : 'transition-none',
                        svarkaCountdownLineClass,
                        svarkaCommitSeconds <= 3 && 'animate-pulse'
                      )}
                      style={{ width: svarkaCountdownWidth }}
                    />
                  </div>
                </div>
              ) : null}
              <div className="mt-3 space-y-2">
                {svarkaCommitSeats.map((seat) => (
                  <div
                    key={`svarka-seat-${seat.seatIndex}`}
                    className="flex items-center justify-between rounded-lg border border-white/10 bg-black/25 px-2 py-1.5"
                  >
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-7 w-7 items-center justify-center overflow-hidden rounded-full border border-white/20 bg-black/35">
                        {seat.isBot ? (
                          <img
                            src="/avatar-for-bot.png"
                            alt=""
                            className="h-full w-full object-cover"
                            draggable={false}
                          />
                        ) : (
                          <Bot className="h-4 w-4 text-neon-cyan" aria-hidden />
                        )}
                      </span>
                      <div className="text-left">
                        <p className="text-[11px] font-semibold text-white">
                          {seat.seatIndex === 0 ? 'Вы' : seat.name}
                        </p>
                        <p className="text-[10px] text-amber-200/70">
                          {seat.canParticipate
                            ? `вход до ${Number(seat.entryAmount ?? 0).toLocaleString('ru-RU')}${
                                Number(seat.entryAmount ?? 0) < svarkaCommitMeta.eff ? ' · all-in' : ''
                              }`
                            : 'нет фишек на столе'}
                        </p>
                      </div>
                    </div>
                    {svarkaCommitPhase === 'collecting' ? (
                      seat.choiceMade ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-amber-300/40 bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-100">
                          <BadgeCheck className="h-3.5 w-3.5" aria-hidden />
                          Выбор сделан
                        </span>
                      ) : (
                        <span className="rounded-full border border-white/20 bg-white/5 px-2 py-0.5 text-[10px] text-muted-foreground">
                          Ожидание…
                        </span>
                      )
                    ) : seat.participateRevealed ? (
                      <span className="rounded-full border border-emerald-300/45 bg-emerald-500/20 px-2 py-0.5 text-[10px] font-semibold text-emerald-100">
                        В игре
                      </span>
                    ) : (
                      <span className="rounded-full border border-red-300/45 bg-red-500/20 px-2 py-0.5 text-[10px] font-semibold text-red-100">
                        Пас
                      </span>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                {heroCanChooseSvarka ? (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        play('svarka-x8-cancel', 1);
                        onSvarkaCommitSkip();
                      }}
                      disabled={svarkaCommitPhase !== 'collecting' || Boolean(heroSvarkaSeat?.choiceMade)}
                      className="flex min-h-[2.75rem] items-center justify-center rounded-lg border border-border bg-card/80 px-2 text-center text-[10px] font-semibold uppercase text-foreground hover:bg-muted/40 sm:text-[11px]"
                    >
                      Пропустить раздачу
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        play('svarka-x8-begin', 1);
                        onSvarkaCommitParticipate();
                      }}
                      disabled={svarkaCommitPhase !== 'collecting' || Boolean(heroSvarkaSeat?.choiceMade)}
                      className="flex min-h-[2.75rem] items-center justify-center rounded-lg border border-amber-400/55 bg-amber-500/20 px-2 text-center text-[10px] font-semibold uppercase text-amber-50 hover:bg-amber-500/30 sm:text-[11px]"
                    >
                      {Boolean(heroSvarkaSeat?.choiceMade)
                        ? 'Выбор принят'
                        : Math.min(svarkaCommitMeta.eff, Number(game.players?.[0]?.chips ?? 0)) <
                            svarkaCommitMeta.eff
                          ? 'Участвовать (All-in)'
                          : 'Участвовать'}
                    </button>
                  </>
                ) : (
                  <p className="col-span-2 rounded-lg border border-white/15 bg-black/20 px-2 py-2 text-center text-[11px] text-amber-100/85">
                    Вы в режиме наблюдения — выбор в сваре недоступен.
                  </p>
                )}
              </div>
            </div>
          </div>
          </div>
        ) : null}

      </main>

      {notifications.map((n) => (
        <LocalCurrencyToast
          key={n.id}
          message={n.message}
          kind={n.kind}
          onClose={() => removeNotification(n.id)}
        />
      ))}

      <TableSeatProfileModal
        open={seatProfileOpen != null}
        onClose={() => setSeatProfileOpen(null)}
        variant={seatProfileOpen?.variant || 'bot'}
        displayName={seatProfileOpen?.displayName}
        chips={seatProfileOpen?.chips}
        initialAvatarUrl={seatProfileOpen?.initialAvatarUrl || ''}
        userId={seatProfileOpen?.userId}
      />
    </div>
  );
}

export default function LocalGame(props) {
  return (
    <AudioProvider>
      <LocalGameInner {...props} />
    </AudioProvider>
  );
}
